import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  ButtonGroup,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Tooltip as MuiTooltip,
  Collapse,
  Divider,
  Link,
  alpha,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewsIcon,
  AddShoppingCart as CartIcon,
  ShoppingBag as PurchaseIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as MoneyIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { TextField, InputAdornment, TableSortLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// Client-side memo for the Overview / All Products reads: going back to a period
// or filter you already viewed renders instantly instead of refetching. The server
// serves past days from pre-aggregated tables and caches today for 5 min, so the
// same TTL here never shows staler numbers than a fresh request would.
const ANALYTICS_CACHE_TTL_MS = 5 * 60 * 1000;
const analyticsGetCache = new Map<string, { at: number; promise: Promise<any> }>();
const cachedGet = (url: string): Promise<any> => {
  const hit = analyticsGetCache.get(url);
  if (hit && Date.now() - hit.at < ANALYTICS_CACHE_TTL_MS) return hit.promise;
  const promise = api.get(url);
  analyticsGetCache.set(url, { at: Date.now(), promise });
  promise.catch(() => analyticsGetCache.delete(url));
  return promise;
};

// --- Types ---
interface TopProduct {
  product_id: string;
  product_title: string;
  product_handle?: string;
  sku?: string | null;
  is_mdoc?: boolean;
  views: number;
  add_to_carts: number;
  purchases: number; // distinct orders that included this product
  units: number;     // total units sold (SUM of quantity)
  revenue: number;
  image_url?: string;
  source?: string;
}

interface BakudanCandidate {
  id: number;
  sku: string;
  handle: string;
  title: string;
  product_type: string | null;
  vendor: string | null;
  image_url: string | null;
  views: number;
  atcs: number;
  qty_7d: number;
  qty_30d: number;
  atc_rate: number;
  accel: number;
  multi_variant_pct: number;
  customer_return_rate: number | null;
  breadth_pct: number;
  anchor_pct: number | null;
  return_rate: number;
  score: number;
  n_atc: number;
  n_accel: number;
  n_multi_variant: number;
  n_customer_return: number;
  n_breadth: number;
  n_anchor: number;
  n_return_rate: number;
  // Discovery signals
  cross_basket_rate: number | null;
  returning_customer_rate: number | null;
  multi_unit_rate: number | null;
  is_proven_bakudan: boolean;
  discovery_score: number | null;
  n_cross_basket: number | null;
  n_returning_customer: number | null;
  n_multi_unit: number | null;
}

type BakudanSortKey = 'score' | 'discovery_score' | 'atc_rate' | 'accel' | 'multi_variant_pct'
  | 'customer_return_rate' | 'breadth_pct' | 'anchor_pct' | 'return_rate' | 'views' | 'qty_30d'
  | 'cross_basket_rate' | 'returning_customer_rate' | 'multi_unit_rate';

interface DailySummary {
  date: string;
  views: number;
  add_to_carts: number;
  checkouts: number;
  purchases: number;
  units?: number;
  revenue?: number;
}

interface TrendingProduct {
  product_id: string;
  product_title: string;
  image_url?: string;
  recent_views: number;
  prior_views: number;
  change_pct: number;
}

interface SearchData {
  top_queries: Array<{ query: string; count: number }>;
  zero_result_queries: Array<{ query: string; count: number }>;
}

interface ShopeeProduct {
  item_id: number;
  item_name: string;
  image_url?: string;
  card_views: number;
  clicks: number;
  page_visitors: number;
  ctr: number;
  add_to_cart_users: number;
  atc_rate: number;
  orders: number;
  order_cvr: number;
  units_sold: number;
  revenue: number;
}

interface ShopeeAnalytics {
  summary: {
    total_card_views?: number;
    total_clicks?: number;
    total_visitors?: number;
    overall_ctr?: number;
    total_atc_users?: number;
    overall_atc_rate?: number;
    total_orders?: number;
    overall_order_cvr?: number;
    total_units?: number;
    total_revenue?: number;
  };
  products: ShopeeProduct[];
  daily: Array<{ date: string; card_views: number; clicks: number; orders: number; revenue: number }>;
}

// Live Shopee GMV from the order sync (shopee_order_revenue) — no manual CSV import, unlike
// ShopeeAnalytics above. Revenue is dated by Shopee's create_time, so it tracks demand on the
// day the order was placed rather than the day it ships (preorder ships 1-2 weeks later).
interface ShopeeRevenueDay {
  date: string;
  orders: number;
  revenue: number;
  units: number;
  cancelled_orders: number;
  cancelled_revenue: number;
}

interface ShopeeRevenue {
  summary: {
    orders?: number;
    revenue?: number;
    units?: number;
    cancelled_orders?: number;
    cancelled_revenue?: number;
    open_orders?: number;
    last_synced_at?: string | null;
    aov?: number;
    cancel_rate?: number;
  };
  daily: ShopeeRevenueDay[];
  products: Array<{ sku: string; item_name: string; units: number; revenue: number; orders: number }>;
}

interface TodayTotals {
  views: number;
  add_to_carts: number;
  checkouts: number;
  purchases: number;
  units?: number;
  revenue: number;
  unique_sessions: number;
}

interface HourlySummary {
  hour: string;
  views: number;
  add_to_carts: number;
  purchases: number;
  revenue?: number;
}

// SKU lead-time row from /api/analytics/sku-lead-time.
// wait_* = customer order → item received; supplier_* = 叫貨 → received
// (supplier metrics only exist since sinsang_cart_log went live 2026-07-02).
interface LeadTimeRow {
  sku: string;
  product_name: string;
  n_received: number;
  wait_p50: number;
  wait_p90: number;
  pct_over_7: number;
  pct_over_14: number;
  last_received_at: string;
  supplier_n: number | null;
  supplier_p50: number | null;
  supplier_p90: number | null;
  image_url: string | null;
}

interface LeadTimeMeta {
  days: number;
  summary: {
    total_items: number;
    wait_p50: number;
    wait_p90: number;
    pct_over_7: number;
    pct_over_14: number;
  };
  sinsang_log: { log_start: string | null; skus_logged: number };
}

interface LooksPerformanceRow {
  look_id: number;
  title: string;
  hero_image_url: string | null;
  status: string;
  occasion: string | null;
  season: string | null;
  derived_styles: string[];
  derived_colors: string[];
  impressions: number;
  clicks: number;
  ctr: number;
  ctr_lb: number;
  avg_position: number | null;
  views: number;
  item_clicks: number;
  atc_count: number;
  atc_sessions: number;
  atc_rate: number;
  purchases: number;
  revenue: number;
  rev_per_1k_impr: number;
}

interface LooksTotals {
  impressions: number;
  clicks: number;
  views: number;
  itemClicks: number;
  atcCount: number;
  purchases: number;
  revenue: number;
}

// --- Design Tokens ---
const COLORS = {
  views: '#3B82F6',
  atc: '#F59E0B',
  purchase: '#10B981',
  revenue: '#8B5CF6',
  gridLine: '#E2E8F0',
  cardBorder: '#E2E8F0',
  liveGreen: '#10B981',
  liveGreenBg: '#ECFDF5',
  tableBgAlt: '#F8FAFC',
  tableHover: '#F1F5F9',
  zeroResult: '#FEF3C7',
  highAtc: '#ECFDF5',
  shopee: '#EE4D2D',
  outlet: '#7C3AED',
  storefront: '#2563EB',
  noke: '#0F766E',
};

// --- Custom Tooltip ---
const CustomTooltip = ({ active, payload, label, isToday }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#1E293B',
      color: '#F8FAFC',
      px: 1.5,
      py: 1,
      borderRadius: 1,
      fontSize: '0.75rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mb: 0.5 }}>
        {isToday ? label : label}
      </Typography>
      {payload.map((p: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.stroke || p.fill }} />
          <span>{p.name}: <strong>
            {p.name === 'Revenue'
              ? `NT$${Math.round(Number(p.value)).toLocaleString()}`
              : Number(p.value).toLocaleString()}
          </strong></span>
        </Box>
      ))}
    </Box>
  );
};

// --- Stat Card Component ---
const StatCard = ({ label, value, icon, color, subtitle, delta, deltaLabel }: {
  label: string; value: string | number; icon: React.ReactNode; color: string; subtitle?: string;
  // Percent change vs the previous window. null/undefined = no comparable prior
  // period, so no chip is drawn (better than implying a change we can't measure).
  delta?: number | null; deltaLabel?: string;
}) => {
  const theme = useTheme();
  const up = (delta ?? 0) >= 0;
  const deltaColor = up ? '#10B981' : '#EF4444';
  return (
    <Card variant="outlined" sx={{
      border: `1px solid ${COLORS.cardBorder}`,
      borderRadius: 2,
      transition: 'box-shadow 0.2s ease',
      '&:hover': { boxShadow: `0 2px 8px ${alpha(color, 0.12)}` },
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
            {delta !== null && delta !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.25,
                  px: 0.6, py: 0.1, borderRadius: 0.75,
                  bgcolor: alpha(deltaColor, 0.12), color: deltaColor,
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {up ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
                </Box>
                {deltaLabel && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                    {deltaLabel}
                  </Typography>
                )}
              </Box>
            )}
            {subtitle && (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            bgcolor: alpha(color, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: color,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Format Date -> YYYY-MM-DD in local time (avoid UTC shift from toISOString)
const toYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// --- Main Component ---
const AnalyticsDashboardPage: React.FC = () => {
  const theme = useTheme();
  const [view, setView] = useState<'overview' | 'all-products' | 'bakudan' | 'lead-time' | 'looks' | 'noke'>('overview');
  const [allProducts, setAllProducts] = useState<TopProduct[]>([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allSortKey, setAllSortKey] = useState<'product_title' | 'views' | 'add_to_carts' | 'atc_rate' | 'purchases' | 'units' | 'revenue' | 'rpv' | 'cvr' | 'potential'>('views');
  const [allSortDir, setAllSortDir] = useState<'asc' | 'desc'>('desc');
  const [allFilter, setAllFilter] = useState('');

  // Bakudan tab state
  const [bakudanCandidates, setBakudanCandidates] = useState<BakudanCandidate[]>([]);
  const [bakudanLoading, setBakudanLoading] = useState(false);
  const [bakudanMeta, setBakudanMeta] = useState<{ cached: boolean; computed_at: string; total: number } | null>(null);
  const [bakudanFilter, setBakudanFilter] = useState('');
  const [bakudanProductType, setBakudanProductType] = useState<string>('');
  const [bakudanVendor, setBakudanVendor] = useState<string>('');
  const [bakudanMinViews, setBakudanMinViews] = useState<number>(50);
  const [bakudanMinAtcRate, setBakudanMinAtcRate] = useState<number>(0.05);
  const [bakudanSortKey, setBakudanSortKey] = useState<BakudanSortKey>('score');

  // Lead Time tab state
  const [leadTimeRows, setLeadTimeRows] = useState<LeadTimeRow[]>([]);
  const [leadTimeMeta, setLeadTimeMeta] = useState<LeadTimeMeta | null>(null);
  const [leadTimeLoading, setLeadTimeLoading] = useState(false);
  const [leadTimeDays, setLeadTimeDays] = useState<number>(120);
  const [leadTimeFilter, setLeadTimeFilter] = useState('');
  const [leadTimeSortKey, setLeadTimeSortKey] = useState<LeadTimeSortKey>('wait_p90');
  const [leadTimeSortDir, setLeadTimeSortDir] = useState<'asc' | 'desc'>('desc');

  // Looks tab state
  const [looksRows, setLooksRows] = useState<LooksPerformanceRow[]>([]);
  const [looksTotals, setLooksTotals] = useState<LooksTotals | null>(null);
  const [looksLoading, setLooksLoading] = useState(false);
  const [looksDays, setLooksDays] = useState<number>(30);
  const [looksFilter, setLooksFilter] = useState('');
  const [looksSortKey, setLooksSortKey] = useState<LooksSortKey>('revenue');
  const [looksSortDir, setLooksSortDir] = useState<'asc' | 'desc'>('desc');
  const [days, setDays] = useState<number | 'today'>(7);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [metric, setMetric] = useState('views');
  const [loading, setLoading] = useState(true);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [dailySummary, setDailySummary] = useState<DailySummary[]>([]);
  const [hourlySummary, setHourlySummary] = useState<HourlySummary[]>([]);
  const [todayTotals, setTodayTotals] = useState<TodayTotals | null>(null);
  const [trending, setTrending] = useState<TrendingProduct[]>([]);
  const [searchData, setSearchData] = useState<SearchData>({ top_queries: [], zero_result_queries: [] });
  const [source, setSource] = useState<string>('all');
  // Brand bucket — server splits on storefront_products.vendor (Metanoia vs MdoC).
  const [brand, setBrand] = useState<'all' | 'metanoia' | 'mdoc'>('all');
  // Same metrics over the immediately preceding window of equal length, so the
  // stat cards can say whether revenue is rising or falling (the whole point of
  // the MdoC split — 30 days of MdoC revenue means nothing without a baseline).
  const [priorSummary, setPriorSummary] = useState<DailySummary[] | null>(null);
  const [shopeeData, setShopeeData] = useState<ShopeeAnalytics | null>(null);
  const [shopeeExpanded, setShopeeExpanded] = useState(false);
  const [shopeeRevenue, setShopeeRevenue] = useState<ShopeeRevenue | null>(null);
  const [shopeeRevenueExpanded, setShopeeRevenueExpanded] = useState(false);
  const [utmData, setUtmData] = useState<Array<{
    utm_source: string; utm_medium: string; utm_campaign: string;
    sessions: number; views: number; add_to_carts: number; purchases: number;
    revenue: number; atc_rate: number; purchase_rate: number;
  }>>([]);
  const [utmExpanded, setUtmExpanded] = useState(false);
  const [upsellData, setUpsellData] = useState<{
    days: number;
    sources: Array<{
      event_source: string;
      impressions: number;
      unique_impression_sessions: number;
      clicks: number;
      adds: number;
      direct_adds?: number;
      purchases: number;
      revenue: number;
      ctr: number;
      atc_rate: number;
      click_to_atc: number;
      atc_to_purchase: number;
    }>;
    mixed_basket?: {
      mixed_orders: number;
      mixed_revenue: number;
      mixed_mdoc_revenue: number;
      mixed_metanoia_revenue: number;
      mdoc_only_orders: number;
      metanoia_only_orders: number;
    };
    products: Array<{
      event_source: string;
      product_handle: string;
      product_title: string;
      impressions: number;
      clicks: number;
      adds: number;
    }>;
  } | null>(null);
  const [upsellExpanded, setUpsellExpanded] = useState(true);
  const [topPicksData, setTopPicksData] = useState<{
    days: number;
    sources: Array<{
      event_source: string;
      impressions: number;
      unique_impression_sessions: number;
      clicks: number;
      adds: number;
      purchases: number;
      revenue: number;
      ctr: number;
      atc_rate: number;
      click_to_atc: number;
      atc_to_purchase: number;
    }>;
    products: Array<{
      event_source: string;
      product_handle: string;
      product_title: string;
      impressions: number;
      clicks: number;
      adds: number;
    }>;
    slots: Array<{
      event_source: string;
      position: number;
      product_id: string | null;
      product_handle: string | null;
      product_title: string | null;
      impressions: number;
      clicks: number;
      adds: number;
    }>;
  } | null>(null);
  const [topPicksExpanded, setTopPicksExpanded] = useState(true);
  const [retention, setRetention] = useState<{
    aov_by_source: Array<{ bucket: string; revenue: number; orders: number; aov: number }>;
    session_cvr: { sessions: number; purchases: number; cvr: number };
    new_vs_returning: Array<{ bucket: string; orders: number; revenue: number }>;
    repeat_window: Array<{ cohort_month: string; first_orders: number; repeated: number; repeat_pct: number; median_days_to_2nd: number }>;
  } | null>(null);
  // Most-wishlisted products — cumulative demand signal (all-time, not date-ranged).
  const [topWishlist, setTopWishlist] = useState<Array<{
    handle: string;
    wishlist_count: number;
    last_added: string;
    product_id: number | null;
    title: string | null;
    status: string | null;
    image_url: string | null;
  }> | null>(null);
  const [wishlistExpanded, setWishlistExpanded] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const rangeActive = Boolean(rangeStart && rangeEnd);
  const isToday = !rangeActive && days === 'today';
  const rangeParams = rangeActive
    ? `&start_date=${toYMD(rangeStart as Date)}&end_date=${toYMD(rangeEnd as Date)}`
    : '';
  // Span in days (inclusive); used as the fallback `days` param for trending/search which
  // don't currently support explicit ranges — gives them a comparable window.
  const rangeSpanDays = rangeActive && rangeStart && rangeEnd
    ? Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / 86400000) + 1)
    : 0;

  // The window immediately before the selected one, same length. Used only for
  // the trend deltas on the stat cards; skipped for "Today" (a partial day
  // against a full yesterday is not a comparison).
  const priorWindow = (() => {
    if (isToday) return null;
    const span = rangeActive ? rangeSpanDays : (days as number);
    const end = new Date();
    if (rangeActive && rangeStart) {
      end.setTime(rangeStart.getTime());
    } else {
      end.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - (span - 1));
    }
    end.setDate(end.getDate() - 1);           // day before the current window
    const start = new Date(end);
    start.setDate(start.getDate() - (span - 1));
    return { start: toYMD(start), end: toYMD(end) };
  })();
  // String form so it can sit in the fetch deps without re-triggering every render.
  const priorParams = priorWindow
    ? `&start_date=${priorWindow.start}&end_date=${priorWindow.end}`
    : '';

  const fetchData = useCallback(async () => {
    if (view === 'all-products' || view === 'lead-time' || view === 'looks' || view === 'noke') { setLoading(false); return; }
    setLoading(true);
    try {
      const sourceParam = source !== 'all' ? `&source=${source}` : '';
      const brandParam = brand !== 'all' ? `&brand=${brand}` : '';

      if (isToday) {
        const [topRes, hourlyRes, totalsRes, searchRes] = await Promise.all([
          cachedGet(`/api/analytics/today/products?metric=${metric}&limit=15${sourceParam}${brandParam}`),
          cachedGet(`/api/analytics/today/summary?${`${sourceParam}${brandParam}`.slice(1)}`),
          cachedGet(`/api/analytics/today/totals?${`${sourceParam}${brandParam}`.slice(1)}`),
          cachedGet('/api/analytics/search?days=1&limit=15')
        ]);
        setTopProducts(topRes.data);
        setHourlySummary(hourlyRes.data);
        setTodayTotals(totalsRes.data);
        setDailySummary([]);
        setTrending([]);
        setSearchData(searchRes.data);
        setPriorSummary(null);
      } else {
        const daysParam = rangeActive ? rangeSpanDays : (days as number);
        const [topRes, dailyRes, priorRes, trendingRes, searchRes] = await Promise.all([
          cachedGet(`/api/analytics/products/top?metric=${metric}&days=${daysParam}&limit=15${sourceParam}${brandParam}${rangeParams}`),
          cachedGet(`/api/analytics/summary/daily?days=${daysParam}${sourceParam}${brandParam}${rangeParams}`),
          // Preceding window of equal length — powers the vs-previous deltas.
          priorParams
            ? cachedGet(`/api/analytics/summary/daily?days=${daysParam}${sourceParam}${brandParam}${priorParams}`).catch(() => null)
            : Promise.resolve(null),
          // Trending compares recent vs prior halves of the window — only meaningful for preset days
          rangeActive
            ? Promise.resolve({ data: [] })
            : cachedGet(`/api/analytics/products/trending?days=${days}&limit=10${brandParam}`),
          cachedGet(`/api/analytics/search?days=${daysParam}&limit=15`)
        ]);
        setTopProducts(topRes.data);
        setDailySummary(dailyRes.data);
        setPriorSummary(priorRes?.data || null);
        setTrending(trendingRes.data);
        setSearchData(searchRes.data);
        setHourlySummary([]);
        setTodayTotals(null);
      }

      // Fetch Shopee + UTM data (separate, don't block main load)
      try {
        const daysParam = isToday ? 1 : (rangeActive ? rangeSpanDays : (days as number));
        const [shopeeRes, shopeeRevRes, utmRes, retentionRes, upsellRes, topPicksRes, wishlistRes] = await Promise.all([
          cachedGet(`/api/analytics/shopee?days=${daysParam}&limit=15${isToday ? '' : rangeParams}`).catch(() => null),
          cachedGet(`/api/analytics/shopee-revenue?days=${daysParam}&limit=15${isToday ? '' : rangeParams}`).catch(() => null),
          cachedGet(`/api/analytics/utm?days=${daysParam}${isToday ? '' : rangeParams}`).catch(() => null),
          cachedGet(`/api/analytics/retention?days=${daysParam}`).catch(() => null),
          cachedGet(`/api/analytics/upsell?days=${daysParam}`).catch(() => null),
          cachedGet(`/api/analytics/collection-top-picks?days=${daysParam}`).catch(() => null),
          // All-time cumulative — not date-ranged. Fetched once per load.
          cachedGet(`/api/storefront/wishlist/admin/top?limit=20`).catch(() => null),
        ]);
        setShopeeData(shopeeRes?.data || null);
        setShopeeRevenue(shopeeRevRes?.data || null);
        setUtmData(utmRes?.data || []);
        setRetention(retentionRes?.data || null);
        setUpsellData(upsellRes?.data || null);
        setTopPicksData(topPicksRes?.data || null);
        setTopWishlist(wishlistRes?.data?.products || null);
      } catch {
        setShopeeData(null);
        setShopeeRevenue(null);
        setUtmData([]);
        setRetention(null);
        setUpsellData(null);
        setTopPicksData(null);
        setTopWishlist(null);
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      enqueueSnackbar('Failed to load analytics data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [view, days, metric, source, brand, isToday, rangeActive, rangeParams, priorParams, rangeSpanDays, enqueueSnackbar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch full product list for the "All Products" tab
  const fetchAllProducts = useCallback(async () => {
    if (view !== 'all-products') return;
    setAllLoading(true);
    try {
      const sourceParam = source !== 'all' ? `&source=${source}` : '';
      const brandParam = brand !== 'all' ? `&brand=${brand}` : '';
      if (isToday) {
        const res = await cachedGet(`/api/analytics/today/products?metric=views&limit=2000${sourceParam}${brandParam}`);
        setAllProducts(res.data || []);
      } else {
        const daysParam = rangeActive ? rangeSpanDays : (days as number);
        const res = await cachedGet(`/api/analytics/products/top?metric=views&days=${daysParam}&limit=2000${sourceParam}${brandParam}${rangeParams}`);
        setAllProducts(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      enqueueSnackbar('Failed to load all products', { variant: 'error' });
    } finally {
      setAllLoading(false);
    }
  }, [view, days, source, brand, isToday, rangeActive, rangeParams, rangeSpanDays, enqueueSnackbar]);

  useEffect(() => { fetchAllProducts(); }, [fetchAllProducts]);

  // Fetch Bakudan candidates (server caches for 1h, so refilters are cheap)
  const fetchBakudan = useCallback(async () => {
    if (view !== 'bakudan') return;
    setBakudanLoading(true);
    try {
      const params = new URLSearchParams();
      if (bakudanProductType) params.set('product_type', bakudanProductType);
      if (bakudanVendor) params.set('vendor', bakudanVendor);
      if (bakudanMinViews > 0) params.set('min_views', String(bakudanMinViews));
      if (bakudanMinAtcRate > 0) params.set('min_atc_rate', String(bakudanMinAtcRate));
      if (bakudanSortKey && bakudanSortKey !== 'score') params.set('sort', bakudanSortKey);
      const res = await api.get(`/api/analytics/bakudan?${params.toString()}`);
      setBakudanCandidates(res.data.candidates || []);
      setBakudanMeta({
        cached: res.data.cached,
        computed_at: res.data.computed_at,
        total: res.data.total,
      });
    } catch (error) {
      console.error('Failed to fetch bakudan:', error);
      enqueueSnackbar('Failed to load Bakudan candidates', { variant: 'error' });
    } finally {
      setBakudanLoading(false);
    }
  }, [view, bakudanProductType, bakudanVendor, bakudanMinViews, bakudanMinAtcRate, bakudanSortKey, enqueueSnackbar]);

  useEffect(() => { fetchBakudan(); }, [fetchBakudan]);

  // Fetch SKU lead times (receiving history → per-SKU supply speed)
  const fetchLeadTime = useCallback(async () => {
    if (view !== 'lead-time') return;
    setLeadTimeLoading(true);
    try {
      const res = await api.get(`/api/analytics/sku-lead-time?days=${leadTimeDays}`);
      setLeadTimeRows(res.data.skus || []);
      setLeadTimeMeta({
        days: res.data.days,
        summary: res.data.summary,
        sinsang_log: res.data.sinsang_log,
      });
    } catch (error) {
      console.error('Failed to fetch SKU lead times:', error);
      enqueueSnackbar('Failed to load SKU lead times', { variant: 'error' });
    } finally {
      setLeadTimeLoading(false);
    }
  }, [view, leadTimeDays, enqueueSnackbar]);

  useEffect(() => { fetchLeadTime(); }, [fetchLeadTime]);

  // Fetch looks funnel performance (impressions → clicks → views → ATC → purchases)
  const fetchLooks = useCallback(async () => {
    if (view !== 'looks') return;
    setLooksLoading(true);
    try {
      const res = await api.get(`/api/analytics/looks-performance?days=${looksDays}`);
      setLooksRows(res.data.looks || []);
      setLooksTotals(res.data.totals || null);
    } catch (error) {
      console.error('Failed to fetch looks performance:', error);
      enqueueSnackbar('Failed to load looks performance', { variant: 'error' });
      setLooksRows([]);
      setLooksTotals(null);
    } finally {
      setLooksLoading(false);
    }
  }, [view, looksDays, enqueueSnackbar]);

  useEffect(() => { fetchLooks(); }, [fetchLooks]);

  // NOKE counter revenue (noke_sales). Its own endpoint and its own tab — never
  // added into the storefront numbers above. Follows the page's date controls.
  const [nokeData, setNokeData] = useState<NokeRevenue | null>(null);
  const [nokeLoading, setNokeLoading] = useState(false);
  const fetchNoke = useCallback(async () => {
    if (view !== 'noke') return;
    setNokeLoading(true);
    try {
      const daysParam = isToday ? 1 : (rangeActive ? rangeSpanDays : (days as number));
      const res = await api.get(`/api/analytics/noke-revenue?days=${daysParam}&limit=20${isToday ? '' : rangeParams}`);
      setNokeData(res.data);
    } catch (error) {
      console.error('Failed to fetch NOKE revenue:', error);
      enqueueSnackbar('Failed to load NOKE revenue', { variant: 'error' });
      setNokeData(null);
    } finally {
      setNokeLoading(false);
    }
  }, [view, isToday, rangeActive, rangeSpanDays, days, rangeParams, enqueueSnackbar]);

  useEffect(() => { fetchNoke(); }, [fetchNoke]);

  // --- Computed Values ---
  // Pull totals from the daily summary (session-deduped) for historical periods, so the
  // stat cards match the trend chart. Top-product sums would double-count sessions that
  // touched multiple products.
  const totalViews = isToday && todayTotals
    ? Number(todayTotals.views)
    : dailySummary.reduce((sum, d) => sum + Number(d.views || 0), 0);
  const totalATCs = isToday && todayTotals
    ? Number(todayTotals.add_to_carts)
    : dailySummary.reduce((sum, d) => sum + Number(d.add_to_carts || 0), 0);
  const totalCheckouts = isToday && todayTotals
    ? Number(todayTotals.checkouts)
    : dailySummary.reduce((sum, d) => sum + Number(d.checkouts || 0), 0);
  const totalPurchases = isToday && todayTotals
    ? Number(todayTotals.purchases)
    : dailySummary.reduce((sum, d) => sum + Number(d.purchases || 0), 0);
  const totalRevenue = isToday && todayTotals
    ? Number(todayTotals.revenue)
    : dailySummary.reduce((sum, d) => sum + Number(d.revenue || 0), 0);
  const atcRate = totalViews > 0 ? (totalATCs / totalViews * 100).toFixed(1) : '0.0';

  // vs the preceding window of equal length. Null when there is no prior data
  // (Today view, or a brand with no history in that window) — we show nothing
  // rather than a fake +100%.
  const sumPrior = (key: keyof DailySummary) =>
    priorSummary ? priorSummary.reduce((sum, d) => sum + Number(d[key] || 0), 0) : null;
  const priorRevenue = sumPrior('revenue');
  const priorPurchases = sumPrior('purchases');
  const pctDelta = (current: number, prior: number | null) => {
    if (prior === null || prior === 0) return null;
    return (current - prior) / prior * 100;
  };
  const revenueDelta = pctDelta(totalRevenue, priorRevenue);
  const purchaseDelta = pctDelta(totalPurchases, priorPurchases);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${(d.getMonth() + 1)}/${d.getDate()}`;
  };

  const formatHour = (hourStr: string) => {
    const d = new Date(hourStr);
    return `${d.getHours().toString().padStart(2, '0')}:00`;
  };

  // pg returns numeric columns as strings — coerce so Recharts scales them.
  const chartData = isToday
    ? hourlySummary.map(h => ({ ...h, label: formatHour(h.hour), views: Number(h.views), add_to_carts: Number(h.add_to_carts), purchases: Number(h.purchases), revenue: Number(h.revenue || 0) }))
    : dailySummary.map(d => ({ ...d, label: formatDate(d.date), revenue: Number(d.revenue || 0) }));

  const truncate = (str: string, maxLen: number) =>
    str && str.length > maxLen ? str.substring(0, maxLen) + '...' : str || '';

  const getAtcRate = (p: TopProduct) =>
    Number(p.views) > 0 ? (Number(p.add_to_carts) / Number(p.views) * 100).toFixed(1) : '0.0';

  const funnelData = [
    { name: 'Views', value: totalViews, color: COLORS.views, pct: '100%' },
    { name: 'Add to Cart', value: totalATCs, color: COLORS.atc, pct: totalViews > 0 ? `${(totalATCs / totalViews * 100).toFixed(1)}%` : '0%' },
    { name: 'Checkout', value: totalCheckouts, color: '#6366F1', pct: totalATCs > 0 ? `${(totalCheckouts / totalATCs * 100).toFixed(1)}%` : '0%' },
    { name: 'Purchase', value: totalPurchases, color: COLORS.purchase, pct: totalCheckouts > 0 ? `${(totalPurchases / totalCheckouts * 100).toFixed(1)}%` : '0%' },
  ];

  const handleMetricChange = (event: SelectChangeEvent) => { setMetric(event.target.value); };

  const periodLabel = isToday
    ? 'today'
    : rangeActive
      ? `${rangeSpanDays}d range`
      : `${days}d`;

  const timeOptions: Array<{ value: number | 'today'; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: 7, label: '7D' },
    { value: 14, label: '14D' },
    { value: 30, label: '30D' },
    { value: 90, label: '90D' },
    { value: 180, label: '180D' },
    { value: 365, label: '1Y' },
  ];

  const selectPreset = (value: number | 'today') => {
    setRangeStart(null);
    setRangeEnd(null);
    setDays(value);
  };

  const clearRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
  };

  // --- Render ---
  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary' }}>
            Product Analytics
          </Typography>
          {rangeActive && rangeStart && rangeEnd && (
            <Chip
              size="small"
              label={`${toYMD(rangeStart)} → ${toYMD(rangeEnd)}`}
              onDelete={clearRange}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ButtonGroup size="small" sx={{
            '& .MuiButton-root': {
              fontSize: '0.75rem',
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              minWidth: 48,
              borderColor: COLORS.cardBorder,
              color: 'text.secondary',
              textTransform: 'none',
              '&.active': {
                bgcolor: theme.palette.primary.main,
                color: '#fff',
                borderColor: theme.palette.primary.main,
                '&:hover': { bgcolor: theme.palette.primary.dark },
              },
            }
          }}>
            {timeOptions.map((opt) => (
              <Button
                key={String(opt.value)}
                className={!rangeActive && days === opt.value ? 'active' : ''}
                onClick={() => selectPreset(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <DatePicker
                label="From"
                value={rangeStart}
                maxDate={rangeEnd || new Date()}
                onChange={(v) => setRangeStart(v)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 140, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } },
                  },
                }}
              />
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>→</Typography>
              <DatePicker
                label="To"
                value={rangeEnd}
                minDate={rangeStart || undefined}
                maxDate={new Date()}
                onChange={(v) => setRangeEnd(v)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 140, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } },
                  },
                }}
              />
              {rangeActive && (
                <IconButton
                  size="small"
                  onClick={clearRange}
                  title="Clear date range"
                  sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5, width: 32, height: 32 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </LocalizationProvider>
          <IconButton
            size="small"
            onClick={view === 'noke' ? fetchNoke : fetchData}
            disabled={view === 'noke' ? nokeLoading : loading}
            sx={{
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 1.5,
              width: 32, height: 32,
              transition: 'transform 0.3s',
              '&:hover': { transform: 'rotate(90deg)' },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* View Tabs */}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        sx={{
          mb: 1,
          minHeight: 40,
          borderBottom: `1px solid ${COLORS.cardBorder}`,
          '& .MuiTabs-indicator': { height: 3 },
          '& .MuiTab-root': {
            minHeight: 40,
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'none',
            py: 0.5,
            px: 2,
            minWidth: 'auto',
          },
        }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="all-products" label="All Products" />
        <Tab value="bakudan" label="Bakudan" />
        <Tab value="lead-time" label="Lead Time" />
        <Tab value="looks" label="Looks" />
        <Tab value="noke" label="NOKE 門市" />
      </Tabs>

      {/* Source + Brand Tabs — hidden on NOKE: counter sales are not a storefront
          source and carry their own brand split inside the tab. */}
      {view !== 'noke' && (<>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      <Tabs
        value={source}
        onChange={(_, v) => setSource(v)}
        sx={{
          minHeight: 36,
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            minHeight: 36,
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'none',
            py: 0.5,
            px: 2,
            minWidth: 'auto',
          },
        }}
      >
        <Tab value="all" label="All Channels" />
        <Tab
          value="storefront"
          label="Storefront"
          icon={<StorefrontIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ '& .MuiTab-iconWrapper': { mr: 0.5 } }}
        />
        <Tab
          value="outlet"
          label="Outlet"
          icon={<StoreIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ '& .MuiTab-iconWrapper': { mr: 0.5 } }}
        />
      </Tabs>

      {/* Brand filter — splits on storefront_products.vendor. Products with no
          storefront row (outlet items, deleted SKUs) fall in the Metanoia bucket. */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={brand}
        onChange={(_, v) => { if (v) setBrand(v); }}
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            py: 0.35,
            px: 1.25,
            borderColor: COLORS.cardBorder,
          },
          '& .Mui-selected': {
            bgcolor: alpha(COLORS.revenue, 0.12),
            color: COLORS.revenue,
          },
        }}
      >
        <ToggleButton value="all">All Brands</ToggleButton>
        <ToggleButton value="metanoia">Metanoia</ToggleButton>
        <ToggleButton value="mdoc">MdoC</ToggleButton>
      </ToggleButtonGroup>
      </Box>

      {/* The brand split only reaches sections keyed on product_id. Say so rather
          than letting all-brand panels read as MdoC numbers. */}
      {brand !== 'all' && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: -1, mb: 2 }}>
          {brand === 'mdoc' ? 'MdoC' : 'Metanoia'} only — stat cards, trend chart, top products and All Products.
          Search, UTM, Retention, Upsell, Top Picks, Wishlist and Shopee panels below stay all-brand.
        </Typography>
      )}
      </>)}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
          <CircularProgress size={32} thickness={4} />
        </Box>
      ) : view === 'overview' ? (
        <>
          {/* Stat Cards */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Views"
                value={totalViews.toLocaleString()}
                icon={<ViewsIcon sx={{ fontSize: 20 }} />}
                color={COLORS.views}
                subtitle={periodLabel}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Add to Cart"
                value={totalATCs.toLocaleString()}
                icon={<CartIcon sx={{ fontSize: 20 }} />}
                color={COLORS.atc}
                subtitle={`${atcRate}% rate`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Purchases"
                value={totalPurchases.toLocaleString()}
                icon={<PurchaseIcon sx={{ fontSize: 20 }} />}
                color={COLORS.purchase}
                subtitle={periodLabel}
                delta={purchaseDelta}
                deltaLabel="vs prev"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Revenue"
                value={`NT$${Math.round(totalRevenue).toLocaleString()}`}
                icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                color={COLORS.revenue}
                subtitle={
                  priorRevenue !== null
                    ? `${periodLabel} · prev NT$${Math.round(priorRevenue).toLocaleString()}`
                    : periodLabel
                }
                delta={revenueDelta}
                deltaLabel="vs prev"
              />
            </Grid>
          </Grid>

          {/* Trend Chart */}
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, mb: 2.5 }}>
            <CardHeader
              title={
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {isToday ? 'Hourly Activity' : 'Daily Trends'}
                </Typography>
              }
              sx={{ pb: 0, pt: 2, px: 2.5 }}
            />
            <CardContent sx={{ height: 280, pt: 1, px: 1.5 }}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.views} stopOpacity={0.15} />
                        <stop offset="95%" stopColor={COLORS.views} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradAtc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.atc} stopOpacity={0.12} />
                        <stop offset="95%" stopColor={COLORS.atc} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradCheckout" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.12} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradPurchase" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.purchase} stopOpacity={0.12} />
                        <stop offset="95%" stopColor={COLORS.purchase} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.revenue} stopOpacity={0.14} />
                        <stop offset="95%" stopColor={COLORS.revenue} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={COLORS.gridLine} strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      dy={8}
                    />
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      dx={-4}
                      domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      dx={4}
                      domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
                    />
                    {/* Revenue lives on its own hidden scale — NT$ is orders of
                        magnitude above session counts and would flatten them. */}
                    <YAxis yAxisId="revenue" hide domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]} />
                    <Tooltip content={<CustomTooltip isToday={isToday} />} />
                    <Area yAxisId="revenue" type="monotone" dataKey="revenue" stroke={COLORS.revenue} strokeWidth={2} fill="url(#gradRevenue)" name="Revenue" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    <Area yAxisId="left" type="monotone" dataKey="views" stroke={COLORS.views} strokeWidth={2} fill="url(#gradViews)" name="Views" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    <Area yAxisId="right" type="monotone" dataKey="add_to_carts" stroke={COLORS.atc} strokeWidth={2} fill="url(#gradAtc)" name="Add to Cart" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    <Area yAxisId="right" type="monotone" dataKey="checkouts" stroke="#6366F1" strokeWidth={2} fill="url(#gradCheckout)" name="Checkout" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                    <Area yAxisId="right" type="monotone" dataKey="purchases" stroke={COLORS.purchase} strokeWidth={2} fill="url(#gradPurchase)" name="Purchases" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled' }}>No data available</Typography>
                </Box>
              )}
            </CardContent>
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 3, px: 2.5, pb: 2, justifyContent: 'center' }}>
              {[
                { label: 'Views', color: COLORS.views },
                { label: 'Add to Cart', color: COLORS.atc },
                { label: 'Checkout', color: '#6366F1' },
                { label: 'Purchases', color: COLORS.purchase },
                { label: 'Revenue', color: COLORS.revenue },
              ].map(({ label, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 3, borderRadius: 1, bgcolor: color }} />
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Card>

          {/* Retention & Revenue Quality */}
          {!isToday && retention && (
            <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, mb: 2.5 }}>
              <CardHeader
                title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Retention & Revenue Quality</Typography>}
                subheader={<Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  AOV by channel · Session CVR · New vs Returning · Repeat-purchase cohorts
                </Typography>}
                sx={{ pb: 1, pt: 2, px: 2.5 }}
              />
              <CardContent sx={{ pt: 0, px: 2.5, pb: 2 }}>
                <Grid container spacing={2}>
                  {/* AOV by source */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, fontWeight: 600 }}>AOV BY CHANNEL</Typography>
                      {retention.aov_by_source.map(b => (
                        <Box key={b.bucket} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                          <Typography sx={{ fontSize: '0.8rem', textTransform: 'capitalize', color: 'text.secondary' }}>{b.bucket}</Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>NT${Number(b.aov || 0).toLocaleString()}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{Number(b.orders).toLocaleString()} orders</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* Session CVR */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, fontWeight: 600 }}>SESSION CVR</Typography>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                        {((Number(retention.session_cvr.cvr) || 0) * 100).toFixed(2)}%
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                        {Number(retention.session_cvr.purchases).toLocaleString()} purchases / {Number(retention.session_cvr.sessions).toLocaleString()} view sessions
                      </Typography>
                    </Box>
                  </Grid>

                  {/* New vs Returning */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, fontWeight: 600 }}>NEW vs RETURNING REVENUE</Typography>
                      {(() => {
                        const total = retention.new_vs_returning.reduce((s, b) => s + Number(b.revenue || 0), 0);
                        return retention.new_vs_returning.map(b => {
                          const pct = total > 0 ? (Number(b.revenue) / total * 100).toFixed(1) : '0.0';
                          return (
                            <Box key={b.bucket} sx={{ mb: 0.75 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '0.8rem', textTransform: 'capitalize', color: 'text.secondary' }}>{b.bucket}</Typography>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>NT${Number(b.revenue).toLocaleString()} ({pct}%)</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{Number(b.orders).toLocaleString()} orders</Typography>
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  </Grid>

                  {/* Repeat purchase cohort */}
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, fontWeight: 600 }}>REPEAT-PURCHASE COHORT (last 12 months by month of 1st order)</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontSize: '0.7rem' }}>Cohort</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>1st orders</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Repeated</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Repeat %</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Median days to 2nd</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {retention.repeat_window.map(r => (
                              <TableRow key={r.cohort_month}>
                                <TableCell sx={{ fontSize: '0.75rem' }}>{r.cohort_month}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{Number(r.first_orders).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{Number(r.repeated).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{Number(r.repeat_pct).toFixed(1)}%</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{r.median_days_to_2nd != null ? `${Number(r.median_days_to_2nd).toFixed(1)}d` : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Top Products & Funnel */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {/* Top Products Table */}
            <Grid item xs={12} md={7}>
              <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, height: '100%' }}>
                <CardHeader
                  title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Top Products</Typography>}
                  action={
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={metric}
                        onChange={handleMetricChange}
                        sx={{ fontSize: '0.75rem', height: 32, '& .MuiSelect-select': { py: 0.5 } }}
                      >
                        <MenuItem value="views" sx={{ fontSize: '0.8rem' }}>Views</MenuItem>
                        <MenuItem value="add_to_carts" sx={{ fontSize: '0.8rem' }}>Add to Carts</MenuItem>
                        <MenuItem value="purchases" sx={{ fontSize: '0.8rem' }}>Orders</MenuItem>
                        <MenuItem value="units" sx={{ fontSize: '0.8rem' }}>Units Sold</MenuItem>
                        <MenuItem value="revenue" sx={{ fontSize: '0.8rem' }}>Revenue</MenuItem>
                      </Select>
                    </FormControl>
                  }
                  sx={{ pb: 0, pt: 2, px: 2 }}
                />
                <CardContent sx={{ overflow: 'auto', maxHeight: 420, p: 0, mt: 1 }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 }, '& td': { fontSize: '0.8rem', py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 2, width: 48 }}></TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">ATC</TableCell>
                          <TableCell align="right">Orders</TableCell>
                          <TableCell align="right">Units</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>ATC%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topProducts.slice(0, 15).map((p, idx) => {
                          const rate = parseFloat(getAtcRate(p));
                          const rpv = Number(p.views) > 0 ? (Number(p.revenue) / Number(p.views)).toFixed(1) : '0.0';
                          return (
                            <TableRow
                              key={p.product_id}
                              sx={{
                                bgcolor: rate > 5 ? COLORS.highAtc : (idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent'),
                                transition: 'background-color 0.15s',
                                '&:hover': { bgcolor: COLORS.tableHover },
                                cursor: 'default',
                              }}
                            >
                              <TableCell sx={{ pl: 2, py: 0.5, width: 48 }}>
                                <Avatar
                                  src={p.image_url || undefined}
                                  variant="rounded"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: alpha('#94A3B8', 0.1),
                                    fontSize: '0.7rem',
                                    color: '#94A3B8',
                                  }}
                                >
                                  {p.product_title?.charAt(0) || '?'}
                                </Avatar>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Box>
                                  <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>{truncate(p.product_title, 30)}</Typography>
                                  {p.source && p.source !== 'storefront' && (
                                    <Chip
                                      label={p.source}
                                      size="small"
                                      sx={{
                                        height: 16,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        bgcolor: alpha(COLORS.outlet, 0.1),
                                        color: COLORS.outlet,
                                        mt: 0.25,
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.views).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.add_to_carts).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.purchases).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.units || 0).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>${Number(p.revenue).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ pr: 2 }}>
                                <Chip
                                  label={`${getAtcRate(p)}%`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: rate > 5 ? alpha(COLORS.purchase, 0.12) : alpha('#64748B', 0.08),
                                    color: rate > 5 ? COLORS.purchase : '#64748B',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {topProducts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No product data yet</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Funnel */}
            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, height: '100%' }}>
                <CardHeader
                  title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Conversion Funnel</Typography>}
                  sx={{ pb: 0, pt: 2, px: 2 }}
                />
                <CardContent sx={{ pt: 2 }}>
                  {totalViews > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {funnelData.map((stage, i) => {
                        const widthPct = totalViews > 0 ? Math.max((stage.value / totalViews) * 100, 4) : 4;
                        return (
                          <Box key={stage.name}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'baseline' }}>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>{stage.name}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                  {stage.value.toLocaleString()}
                                </Typography>
                                {i > 0 && (
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                                    {stage.pct}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ width: '100%', height: 28, bgcolor: '#F1F5F9', borderRadius: 1.5, overflow: 'hidden' }}>
                              <Box sx={{
                                width: `${widthPct}%`,
                                height: '100%',
                                bgcolor: stage.color,
                                borderRadius: 1.5,
                                transition: 'width 0.6s ease',
                                opacity: 0.85,
                              }} />
                            </Box>
                          </Box>
                        );
                      })}
                      {/* Conversion summary */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1.5, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.atc }}>{atcRate}%</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>View to Cart</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.purchase }}>
                            {totalViews > 0 ? (totalPurchases / totalViews * 100).toFixed(1) : '0.0'}%
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>View to Purchase</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: COLORS.revenue }}>
                            {totalATCs > 0 ? (totalPurchases / totalATCs * 100).toFixed(1) : '0.0'}%
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>Cart to Purchase</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No data yet</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Trending & Search */}
          <Grid container spacing={2}>
            {!isToday && !rangeActive && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <TrendingUpIcon sx={{ fontSize: 18, color: COLORS.purchase }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Trending Products</Typography>
                      </Box>
                    }
                    sx={{ pb: 0, pt: 2, px: 2 }}
                  />
                  <CardContent sx={{ overflow: 'auto', maxHeight: 380, p: 0, mt: 1 }}>
                    <TableContainer>
                      <Table size="small" sx={{ '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 }, '& td': { fontSize: '0.8rem', py: 1 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ pl: 2, width: 40 }}></TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Recent</TableCell>
                            <TableCell align="right">Prior</TableCell>
                            <TableCell align="right" sx={{ pr: 2 }}>Change</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {trending.map((t, idx) => (
                            <TableRow key={t.product_id} sx={{ bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent', '&:hover': { bgcolor: COLORS.tableHover } }}>
                              <TableCell sx={{ pl: 2, py: 0.5, width: 40 }}>
                                <Avatar
                                  src={(t as any).image_url || undefined}
                                  variant="rounded"
                                  sx={{ width: 32, height: 32, bgcolor: alpha('#94A3B8', 0.1), fontSize: '0.65rem', color: '#94A3B8' }}
                                >
                                  {t.product_title?.charAt(0) || '?'}
                                </Avatar>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {truncate(t.product_title, 25)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(t.recent_views).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{Number(t.prior_views).toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ pr: 2 }}>
                                <Chip
                                  icon={Number(t.change_pct) >= 0 ? <TrendingUpIcon sx={{ fontSize: '14px !important' }} /> : <TrendingDownIcon sx={{ fontSize: '14px !important' }} />}
                                  label={`${Number(t.change_pct) >= 0 ? '+' : ''}${Number(t.change_pct).toFixed(0)}%`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: Number(t.change_pct) >= 0 ? alpha(COLORS.purchase, 0.1) : alpha('#EF4444', 0.1),
                                    color: Number(t.change_pct) >= 0 ? COLORS.purchase : '#EF4444',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {trending.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No trending data</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Search Intelligence */}
            <Grid item xs={12} md={isToday || rangeActive ? 12 : 6}>
              <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <SearchIcon sx={{ fontSize: 18, color: '#64748B' }} />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Search Queries</Typography>
                    </Box>
                  }
                  sx={{ pb: 0, pt: 2, px: 2 }}
                />
                <CardContent sx={{ overflow: 'auto', maxHeight: 380, p: 0, mt: 1 }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 }, '& td': { fontSize: '0.8rem', py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 2 }}>Query</TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchData.top_queries.map((q, i) => (
                          <TableRow key={i} sx={{ bgcolor: i % 2 === 1 ? COLORS.tableBgAlt : 'transparent', '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2 }}>{q.query}</TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums' }}>{Number(q.count).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        {searchData.top_queries.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No search data yet</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {searchData.zero_result_queries.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ px: 2, py: 1, bgcolor: COLORS.zeroResult, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#92400E' }}>
                          Zero-Result Searches
                        </Typography>
                      </Box>
                      <TableContainer>
                        <Table size="small" sx={{ '& td': { fontSize: '0.8rem', py: 0.75 } }}>
                          <TableBody>
                            {searchData.zero_result_queries.map((q, i) => (
                              <TableRow key={i} sx={{ bgcolor: alpha('#F59E0B', 0.04), '&:hover': { bgcolor: alpha('#F59E0B', 0.08) } }}>
                                <TableCell sx={{ pl: 2 }}>{q.query}</TableCell>
                                <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums' }}>{Number(q.count).toLocaleString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Shopee Revenue — live from the order sync, no manual import */}
          {shopeeRevenue && shopeeRevenue.daily.length > 0 && (
            <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, mt: 2.5 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: COLORS.shopee, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StoreIcon sx={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Shopee Revenue</Typography>
                    <Chip
                      label={`NT$${Number(shopeeRevenue.summary.revenue || 0).toLocaleString()}`}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.shopee, 0.1), color: COLORS.shopee }}
                    />
                    <MuiTooltip title="Buyer-paid GMV from the live order sync, dated by Shopee's order create_time. Not net payout — Shopee fees and seller vouchers settle later.">
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>GMV · live sync</Typography>
                    </MuiTooltip>
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setShopeeRevenueExpanded(!shopeeRevenueExpanded)}>
                    {shopeeRevenueExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                }
                sx={{ pb: 0, pt: 2, px: 2 }}
              />
              <CardContent sx={{ pt: 1.5, pb: 2 }}>
                <Grid container spacing={2}>
                  {[
                    { label: 'Orders', value: Number(shopeeRevenue.summary.orders || 0).toLocaleString() },
                    { label: 'Units', value: Number(shopeeRevenue.summary.units || 0).toLocaleString() },
                    { label: 'AOV', value: `NT$${Number(shopeeRevenue.summary.aov || 0).toLocaleString()}` },
                    { label: 'Awaiting ship', value: Number(shopeeRevenue.summary.open_orders || 0).toLocaleString() },
                    { label: 'Cancelled', value: Number(shopeeRevenue.summary.cancelled_orders || 0).toLocaleString() },
                    {
                      label: 'Cancel rate',
                      value: `${((Number(shopeeRevenue.summary.cancel_rate) || 0) * 100).toFixed(1)}%`,
                    },
                  ].map(({ label, value }) => (
                    <Grid item xs={4} md={2} key={label}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                          {value}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25 }}>{label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ height: 200, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={shopeeRevenue.daily.map(d => ({
                        // Backend returns YYYY-MM-DD strings (already Asia/Taipei) — slice, don't
                        // re-parse, so the browser timezone can't shift a day.
                        label: d.date.slice(5),
                        revenue: Number(d.revenue) || 0,
                        cancelled: Number(d.cancelled_revenue) || 0,
                      }))}
                      margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                    >
                      <CartesianGrid stroke={COLORS.gridLine} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94A3B8' }} dy={8} />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#94A3B8' }}
                        dx={-4}
                        tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                      />
                      <Tooltip
                        formatter={(v: number, name: string) => [`NT$${Number(v).toLocaleString()}`, name === 'revenue' ? 'Revenue' : 'Cancelled']}
                        contentStyle={{ fontSize: '0.75rem', borderRadius: 8, border: `1px solid ${COLORS.cardBorder}` }}
                      />
                      <Bar dataKey="revenue" fill={COLORS.shopee} radius={[3, 3, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="cancelled" fill={alpha(COLORS.shopee, 0.28)} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>

              <Collapse in={shopeeRevenueExpanded}>
                <Divider />
                <CardContent sx={{ overflow: 'auto', maxHeight: 400, p: 0 }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 }, '& td': { fontSize: '0.8rem', py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 2 }}>SKU</TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Orders</TableCell>
                          <TableCell align="right">Units</TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shopeeRevenue.products.map((p, idx) => (
                          <TableRow key={`${p.sku}-${idx}`} sx={{ bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent', '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.sku}</TableCell>
                            <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.item_name}
                            </TableCell>
                            <TableCell align="right">{Number(p.orders).toLocaleString()}</TableCell>
                            <TableCell align="right">{Number(p.units).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontWeight: 600 }}>
                              NT${Number(p.revenue).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', px: 2, py: 1.5 }}>
                    Per-SKU revenue is item price x quantity (pre-voucher); the order total above is authoritative.
                  </Typography>
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* Shopee Analytics */}
          {shopeeData && shopeeData.products.length > 0 && (
            <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2, mt: 2.5 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: COLORS.shopee, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StoreIcon sx={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Shopee Performance</Typography>
                    {shopeeData.summary.total_revenue && (
                      <Chip
                        label={`NT$${Number(shopeeData.summary.total_revenue).toLocaleString()}`}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.shopee, 0.1), color: COLORS.shopee }}
                      />
                    )}
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setShopeeExpanded(!shopeeExpanded)}>
                    {shopeeExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                }
                sx={{ pb: 0, pt: 2, px: 2 }}
              />
              {/* Summary stats always visible */}
              <CardContent sx={{ pt: 1.5, pb: shopeeExpanded ? 0 : 2 }}>
                <Grid container spacing={2}>
                  {[
                    { label: 'Card Views', value: shopeeData.summary.total_card_views },
                    { label: 'CTR', value: shopeeData.summary.overall_ctr ? `${(Number(shopeeData.summary.overall_ctr) * 100).toFixed(1)}%` : '0%' },
                    { label: 'ATC Rate', value: shopeeData.summary.overall_atc_rate ? `${(Number(shopeeData.summary.overall_atc_rate) * 100).toFixed(1)}%` : '0%' },
                    { label: 'Order CVR', value: shopeeData.summary.overall_order_cvr ? `${(Number(shopeeData.summary.overall_order_cvr) * 100).toFixed(1)}%` : '0%' },
                    { label: 'Orders', value: shopeeData.summary.total_orders },
                    { label: 'Units Sold', value: shopeeData.summary.total_units },
                  ].map(({ label, value }) => (
                    <Grid item xs={4} md={2} key={label}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                          {typeof value === 'number' ? Number(value).toLocaleString() : value || '0'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.25 }}>{label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
              <Collapse in={shopeeExpanded}>
                <Divider />
                <CardContent sx={{ overflow: 'auto', maxHeight: 400, p: 0 }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 }, '& td': { fontSize: '0.8rem', py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ pl: 2, width: 48 }}></TableCell>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">CTR</TableCell>
                          <TableCell align="right">ATC</TableCell>
                          <TableCell align="right">Orders</TableCell>
                          <TableCell align="right">CVR</TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shopeeData.products.map((sp, idx) => (
                          <TableRow key={sp.item_id} sx={{ bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent', '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2, py: 0.5 }}>
                              <Avatar
                                src={sp.image_url || undefined}
                                variant="rounded"
                                sx={{ width: 36, height: 36, bgcolor: alpha(COLORS.shopee, 0.1), fontSize: '0.7rem', color: COLORS.shopee }}
                              >
                                {sp.item_name?.charAt(0) || '?'}
                              </Avatar>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {truncate(sp.item_name, 25)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(sp.card_views).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(sp.clicks).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip label={`${(Number(sp.ctr) * 100).toFixed(1)}%`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.shopee, 0.08), color: COLORS.shopee }} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(sp.add_to_cart_users).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(sp.orders).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip label={`${(Number(sp.order_cvr) * 100).toFixed(1)}%`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: Number(sp.order_cvr) > 0.03 ? alpha(COLORS.purchase, 0.1) : alpha('#64748B', 0.08), color: Number(sp.order_cvr) > 0.03 ? COLORS.purchase : '#64748B' }} />
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>${Number(sp.revenue).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* UTM Source Breakdown */}
          {utmData.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      Traffic Sources (UTM)
                    </Typography>
                    <Chip label={`${utmData.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.views, 0.1), color: COLORS.views }} />
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setUtmExpanded(!utmExpanded)}>
                    {utmExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
                sx={{ py: 1.5, px: 2, cursor: 'pointer' }}
                onClick={() => setUtmExpanded(!utmExpanded)}
              />
              <Collapse in={utmExpanded}>
                <CardContent sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', pl: 2 }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Medium</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Campaign</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Sessions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Views</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Purchases</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>CVR</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', pr: 2 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {utmData.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt }, '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2, fontWeight: 600, fontSize: '0.78rem' }}>
                              {row.utm_source}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {row.utm_medium || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.utm_campaign || '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.sessions).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.views).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.add_to_carts).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip label={`${(Number(row.atc_rate || 0) * 100).toFixed(1)}%`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.atc, 0.1), color: COLORS.atc }} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.purchases).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip label={`${(Number(row.purchase_rate || 0) * 100).toFixed(1)}%`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: Number(row.purchase_rate) > 0.02 ? alpha(COLORS.purchase, 0.1) : alpha('#64748B', 0.08), color: Number(row.purchase_rate) > 0.02 ? COLORS.purchase : '#64748B' }} />
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>${Number(row.revenue).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* Most Wishlisted Products — cumulative demand signal (all-time) from
              the server-backed wishlist. Surfaces what logged-in customers save
              but haven't bought yet — re-engagement + restock-priority signal. */}
          {topWishlist && topWishlist.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      Most Wishlisted Products
                    </Typography>
                    <Chip
                      label={`top ${topWishlist.length}`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.atc, 0.1), color: COLORS.atc }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      saved by logged-in customers (all-time)
                    </Typography>
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setWishlistExpanded(!wishlistExpanded)}>
                    {wishlistExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
                sx={{ py: 1.5, px: 2, cursor: 'pointer' }}
                onClick={() => setWishlistExpanded(!wishlistExpanded)}
              />
              <Collapse in={wishlistExpanded}>
                <CardContent sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Saved by</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Last added</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topWishlist.map((p, i) => (
                          <TableRow key={p.handle} hover>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{i + 1}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {p.image_url && (
                                  <Box
                                    component="img"
                                    src={p.image_url}
                                    alt=""
                                    sx={{ width: 32, height: 40, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }}
                                  />
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                                    {p.title || p.handle}
                                  </Typography>
                                  {p.status && p.status !== 'active' && (
                                    <Chip label={p.status} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: COLORS.atc }}>
                              {p.wishlist_count}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                              {p.last_added ? new Date(p.last_added).toLocaleDateString() : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* Cart Upsell Performance — measures cart-drawer cross-sell strips */}
          {upsellData && upsellData.sources.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      Cart Upsell Performance
                    </Typography>
                    <Chip
                      label={`${upsellData.sources.length} strip${upsellData.sources.length === 1 ? '' : 's'}`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.atc, 0.1), color: COLORS.atc }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      impression → click → add → purchase
                    </Typography>
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setUpsellExpanded(!upsellExpanded)}>
                    {upsellExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
                sx={{ py: 1.5, px: 2, cursor: 'pointer' }}
                onClick={() => setUpsellExpanded(!upsellExpanded)}
              />
              <Collapse in={upsellExpanded}>
                <CardContent sx={{ p: 0 }}>
                  {/* Mixed-basket banner — soft attribution showing cross-brand
                      orders. More forgiving than strict session+product attribution
                      below, captures cases where customer saw the cart strip, then
                      completed purchase later (different session) or via a different
                      path (browsed to PDP first). For Metanoia↔MdoC the cart drawer
                      is the primary discovery surface. */}
                  {upsellData.mixed_basket && upsellData.mixed_basket.mixed_orders > 0 && (
                    <Box sx={{
                      mx: 2, mt: 1.5, p: 1.5, borderRadius: 1,
                      bgcolor: alpha(COLORS.atc, 0.06),
                      border: `1px solid ${alpha(COLORS.atc, 0.18)}`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.atc }}>
                          Mixed-basket attribution
                        </Typography>
                        <Chip
                          label={`${upsellData.mixed_basket.mixed_orders} order${upsellData.mixed_basket.mixed_orders === 1 ? '' : 's'} contain both brands`}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.atc, 0.15), color: COLORS.atc }}
                        />
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                          cart drawer is the primary cross-brand discovery surface; strong soft signal
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total basket revenue</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            NT${Number(upsellData.mixed_basket.mixed_revenue).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MdoC line revenue</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: COLORS.atc }}>
                            NT${Number(upsellData.mixed_basket.mixed_mdoc_revenue).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Metanoia line revenue</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            NT${Number(upsellData.mixed_basket.mixed_metanoia_revenue).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Single-brand orders</Typography>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                            {upsellData.mixed_basket.metanoia_only_orders} Metanoia · {upsellData.mixed_basket.mdoc_only_orders} MdoC
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', pl: 2 }}>Strip</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Impressions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Sessions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Clicks</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>CTR</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATCs (wide)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Purchases</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC→Purchase</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', pr: 2 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upsellData.sources.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt }, '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2, fontWeight: 600, fontSize: '0.78rem' }}>
                              {row.event_source}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.impressions).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.unique_impression_sessions).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.clicks).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.ctr) * 100).toFixed(1)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.ctr > 0.05 ? alpha(COLORS.views, 0.1) : alpha('#64748B', 0.08),
                                  color: row.ctr > 0.05 ? COLORS.views : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.adds).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.atc_rate) * 100).toFixed(2)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.atc_rate > 0.02 ? alpha(COLORS.atc, 0.1) : alpha('#64748B', 0.08),
                                  color: row.atc_rate > 0.02 ? COLORS.atc : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.purchases).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.atc_to_purchase) * 100).toFixed(1)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.atc_to_purchase > 0.3 ? alpha(COLORS.purchase, 0.1) : alpha('#64748B', 0.08),
                                  color: row.atc_to_purchase > 0.3 ? COLORS.purchase : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>${Number(row.revenue).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {upsellData.products.length > 0 && (
                    <Box sx={{ px: 2, pt: 2, pb: 1, borderTop: `1px solid ${COLORS.cardBorder}`, mt: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                        Top products by strip impressions
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Strip</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Product</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Impressions</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Clicks</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Adds</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {upsellData.products.slice(0, 15).map((p, i) => (
                              <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt } }}>
                                <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{p.event_source}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.product_title}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.impressions).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.clicks).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.adds).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: p.adds > 0 ? COLORS.atc : 'text.disabled' }}>
                                  {p.impressions > 0 ? `${(p.adds / p.impressions * 100).toFixed(2)}%` : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          )}

          {/* Collection Top Picks Performance — measures the horizontal strip
              at the top of each collection page (event_source = collection_top_picks_{handle}) */}
          {topPicksData && topPicksData.sources.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2.5, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                      Collection Top Picks Performance
                    </Typography>
                    <Chip
                      label={`${topPicksData.sources.length} strip${topPicksData.sources.length === 1 ? '' : 's'}`}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(COLORS.atc, 0.1), color: COLORS.atc }}
                    />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      impression → click → add → purchase, per collection
                    </Typography>
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => setTopPicksExpanded(!topPicksExpanded)}>
                    {topPicksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                }
                sx={{ py: 1.5, px: 2, cursor: 'pointer' }}
                onClick={() => setTopPicksExpanded(!topPicksExpanded)}
              />
              <Collapse in={topPicksExpanded}>
                <CardContent sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', pl: 2 }}>Collection</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Impressions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Sessions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Clicks</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>CTR</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Add to Cart</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Purchases</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC→Purchase</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', pr: 2 }}>Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topPicksData.sources.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt }, '&:hover': { bgcolor: COLORS.tableHover } }}>
                            <TableCell sx={{ pl: 2, fontWeight: 600, fontSize: '0.78rem' }}>
                              {row.event_source.replace(/^collection_top_picks_/, '') || row.event_source}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.impressions).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.unique_impression_sessions).toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.clicks).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.ctr) * 100).toFixed(1)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.ctr > 0.05 ? alpha(COLORS.views, 0.1) : alpha('#64748B', 0.08),
                                  color: row.ctr > 0.05 ? COLORS.views : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.adds).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.atc_rate) * 100).toFixed(2)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.atc_rate > 0.02 ? alpha(COLORS.atc, 0.1) : alpha('#64748B', 0.08),
                                  color: row.atc_rate > 0.02 ? COLORS.atc : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(row.purchases).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${(Number(row.atc_to_purchase) * 100).toFixed(1)}%`}
                                size="small"
                                sx={{
                                  height: 18, fontSize: '0.65rem', fontWeight: 600,
                                  bgcolor: row.atc_to_purchase > 0.3 ? alpha(COLORS.purchase, 0.1) : alpha('#64748B', 0.08),
                                  color: row.atc_to_purchase > 0.3 ? COLORS.purchase : '#64748B',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>${Number(row.revenue).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {topPicksData.slots.length > 0 && (
                    <Box sx={{ px: 2, pt: 2, pb: 1, borderTop: `1px solid ${COLORS.cardBorder}`, mt: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                        Per-slot performance (does higher position = higher CTR?)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Collection</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Pos</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Product</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Impressions</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Clicks</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Adds</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>CTR</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topPicksData.slots.slice(0, 200).map((s, i) => (
                              <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt } }}>
                                <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                  {s.event_source.replace(/^collection_top_picks_/, '') || s.event_source}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{s.position}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {s.product_title || (s.product_handle ? s.product_handle : <span style={{ color: '#94A3B8' }}>—</span>)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(s.impressions).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(s.clicks).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(s.adds).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: s.clicks > 0 ? COLORS.views : 'text.disabled' }}>
                                  {s.impressions > 0 ? `${(s.clicks / s.impressions * 100).toFixed(2)}%` : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: s.adds > 0 ? COLORS.atc : 'text.disabled' }}>
                                  {s.impressions > 0 ? `${(s.adds / s.impressions * 100).toFixed(2)}%` : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {topPicksData.products.length > 0 && (
                    <Box sx={{ px: 2, pt: 2, pb: 1, borderTop: `1px solid ${COLORS.cardBorder}`, mt: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                        Top products by strip impressions
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Collection</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Product</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Impressions</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Clicks</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Adds</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>ATC Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topPicksData.products.slice(0, 15).map((p, i) => (
                              <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt } }}>
                                <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                  {p.event_source.replace(/^collection_top_picks_/, '') || p.event_source}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {p.product_title}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.impressions).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.clicks).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.adds).toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: p.adds > 0 ? COLORS.atc : 'text.disabled' }}>
                                  {p.impressions > 0 ? `${(p.adds / p.impressions * 100).toFixed(2)}%` : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          )}
        </>
      ) : view === 'all-products' ? (
        <AllProductsTable
          loading={allLoading}
          products={allProducts}
          sortKey={allSortKey}
          sortDir={allSortDir}
          onSort={(k) => {
            if (k === allSortKey) {
              setAllSortDir(allSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setAllSortKey(k);
              setAllSortDir(k === 'product_title' ? 'asc' : 'desc');
            }
          }}
          filter={allFilter}
          onFilterChange={setAllFilter}
          periodLabel={periodLabel}
        />
      ) : view === 'noke' ? (
        <NokeRevenuePanel loading={nokeLoading} data={nokeData} periodLabel={periodLabel} />
      ) : view === 'looks' ? (
        <LooksPerformanceTable
          loading={looksLoading}
          rows={looksRows}
          totals={looksTotals}
          days={looksDays}
          onDaysChange={setLooksDays}
          filter={looksFilter}
          onFilterChange={setLooksFilter}
          sortKey={looksSortKey}
          sortDir={looksSortDir}
          onSort={(k) => {
            if (k === looksSortKey) {
              setLooksSortDir(looksSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setLooksSortKey(k);
              setLooksSortDir(k === 'title' ? 'asc' : 'desc');
            }
          }}
        />
      ) : view === 'lead-time' ? (
        <LeadTimeTable
          loading={leadTimeLoading}
          rows={leadTimeRows}
          meta={leadTimeMeta}
          days={leadTimeDays}
          onDaysChange={setLeadTimeDays}
          filter={leadTimeFilter}
          onFilterChange={setLeadTimeFilter}
          sortKey={leadTimeSortKey}
          sortDir={leadTimeSortDir}
          onSort={(k) => {
            if (k === leadTimeSortKey) {
              setLeadTimeSortDir(leadTimeSortDir === 'asc' ? 'desc' : 'asc');
            } else {
              setLeadTimeSortKey(k);
              setLeadTimeSortDir(k === 'product_name' || k === 'sku' ? 'asc' : 'desc');
            }
          }}
        />
      ) : (
        <BakudanTable
          loading={bakudanLoading}
          candidates={bakudanCandidates}
          meta={bakudanMeta}
          filter={bakudanFilter}
          onFilterChange={setBakudanFilter}
          productType={bakudanProductType}
          onProductTypeChange={setBakudanProductType}
          vendor={bakudanVendor}
          onVendorChange={setBakudanVendor}
          minViews={bakudanMinViews}
          onMinViewsChange={setBakudanMinViews}
          minAtcRate={bakudanMinAtcRate}
          onMinAtcRateChange={setBakudanMinAtcRate}
          sortKey={bakudanSortKey}
          onSortChange={setBakudanSortKey}
        />
      )}
    </Box>
  );
};

// --- All Products Table ---
type AllSortKey = 'product_title' | 'views' | 'add_to_carts' | 'atc_rate' | 'purchases' | 'units' | 'revenue' | 'rpv' | 'cvr' | 'potential';

// Raw ATC Rate is unusable for finding under-exposed winners: a product with 1
// view and 1 ATC shows 100%. POTENTIAL is the Wilson score lower bound of the
// ATC rate — the worst-case true rate at 95% confidence given the sample size —
// so a product only scores high when it is both good AND has enough data.
//
// The view floors matter more than the formula. To *pass* the test at n=5 a
// product must show 2/5 = 40% observed, so anything surviving at tiny n
// necessarily looks spectacular (winner's curse). At ~50 views, passing means
// showing ~20% — believable, and close to the truth you will actually get.
// 50 views is where a 1.8x lift over an ~11% baseline becomes detectable;
// below 20 views nothing is knowable and we show no score at all.
const POTENTIAL_MIN_JUDGE = 50;   // full verdict
const POTENTIAL_MIN_LEAD = 20;    // weak signal — a lead, not a verdict

const wilsonLowerBound = (successes: number, trials: number, z = 1.96): number => {
  if (trials <= 0) return 0;
  const p = successes / trials;
  const denom = 1 + (z * z) / trials;
  const centre = (p + (z * z) / (2 * trials)) / denom;
  const margin = (z * Math.sqrt((p * (1 - p)) / trials + (z * z) / (4 * trials * trials))) / denom;
  return Math.max(0, centre - margin);
};

type AllPreset = 'all' | 'gems' | 'untested';

// Live PDP for a row. Outlet rows are warehouse items with no storefront page,
// and a row can arrive without a handle, so both return null (no link).
const productUrl = (p: { product_handle?: string; is_mdoc?: boolean; source?: string }): string | null => {
  if (!p.product_handle || p.source === 'outlet') return null;
  const host = p.is_mdoc ? 'https://mdoc.tw' : 'https://metanoia.tw';
  return `${host}/shop/${encodeURIComponent(p.product_handle)}`;
};

const AllProductsTable: React.FC<{
  loading: boolean;
  products: TopProduct[];
  sortKey: AllSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: AllSortKey) => void;
  filter: string;
  onFilterChange: (v: string) => void;
  periodLabel: string;
}> = ({ loading, products, sortKey, sortDir, onSort, filter, onFilterChange, periodLabel }) => {
  const [preset, setPreset] = useState<AllPreset>('all');
  // Chips set a default sort; clicking any column header hands control back.
  const [presetSort, setPresetSort] = useState<{ key: AllSortKey; dir: 'asc' | 'desc' } | null>(null);

  const computed = products.map(p => {
    const views = Number(p.views || 0);
    const atc = Number(p.add_to_carts || 0);
    const purchases = Number(p.purchases || 0);
    const revenue = Number(p.revenue || 0);
    return {
      ...p,
      _views: views,
      _atc: atc,
      _purchases: purchases,
      _units: Number(p.units || 0),
      _revenue: revenue,
      _atc_rate: views > 0 ? atc / views : 0,
      _cvr: views > 0 ? purchases / views : 0,
      _rpv: views > 0 ? revenue / views : 0,
      _lb: wilsonLowerBound(atc, views),
      _judgeable: views >= POTENTIAL_MIN_JUDGE,
      _lead: views >= POTENTIAL_MIN_LEAD && views < POTENTIAL_MIN_JUDGE,
    };
  });

  // Site-wide ATC baseline for this period — computed over every product, not
  // the filtered subset, so the reference does not move when you filter.
  const baseViews = computed.reduce((a, p) => a + p._views, 0);
  const baseAtc = computed.reduce((a, p) => a + p._atc, 0);
  const baseline = baseViews > 0 ? baseAtc / baseViews : 0;

  const searched = filter
    ? computed.filter(p =>
        (p.product_title || '').toLowerCase().includes(filter.toLowerCase()) ||
        (p.sku || '').includes(filter.trim()))
    : computed;

  const gemCount = computed.filter(p => p._judgeable && p._lb > baseline).length;
  const untestedCount = computed.filter(p => p._views < POTENTIAL_MIN_LEAD).length;

  const filtered =
    preset === 'gems' ? searched.filter(p => p._judgeable && p._lb > baseline)
    : preset === 'untested' ? searched.filter(p => p._views < POTENTIAL_MIN_LEAD)
    : searched;

  const effKey = presetSort ? presetSort.key : sortKey;
  const effDir = presetSort ? presetSort.dir : sortDir;

  const applyPreset = (next: AllPreset) => {
    setPreset(next);
    // Gems are ranked by revenue per view — that is what ad money buys.
    if (next === 'gems') setPresetSort({ key: 'rpv', dir: 'desc' });
    else if (next === 'untested') setPresetSort({ key: 'views', dir: 'desc' });
    else setPresetSort(null);
  };

  const sorted = [...filtered].sort((a, b) => {
    const dir = effDir === 'asc' ? 1 : -1;
    if (effKey === 'product_title') {
      return (a.product_title || '').localeCompare(b.product_title || '') * dir;
    }
    // Unjudgeable rows (< MIN_LEAD views) carry no score, so they sink to the
    // bottom in either direction rather than polluting the top of the sort.
    const potential = (p: typeof a) => (p._views >= POTENTIAL_MIN_LEAD ? p._lb : -1);
    const map: Record<Exclude<AllSortKey, 'product_title'>, number> = {
      views: a._views - b._views,
      add_to_carts: a._atc - b._atc,
      atc_rate: a._atc_rate - b._atc_rate,
      purchases: a._purchases - b._purchases,
      units: a._units - b._units,
      revenue: a._revenue - b._revenue,
      rpv: a._rpv - b._rpv,
      cvr: a._cvr - b._cvr,
      potential: potential(a) - potential(b),
    };
    return map[effKey as Exclude<AllSortKey, 'product_title'>] * dir;
  });

  const totals = filtered.reduce((acc, p) => ({
    views: acc.views + p._views,
    atc: acc.atc + p._atc,
    purchases: acc.purchases + p._purchases,
    units: acc.units + p._units,
    revenue: acc.revenue + p._revenue,
  }), { views: 0, atc: 0, purchases: 0, units: 0, revenue: 0 });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  const truncate = (str: string, maxLen: number) =>
    str && str.length > maxLen ? str.substring(0, maxLen) + '…' : str || '';

  const headCell = (key: AllSortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ pr: align === 'right' ? 2 : undefined, pl: align === 'left' ? 2 : undefined }}>
      <TableSortLabel
        active={effKey === key}
        direction={effKey === key ? effDir : 'desc'}
        onClick={() => { setPresetSort(null); onSort(key); }}
        sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary !important', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>All Products</Typography>
            <Chip
              label={`${filtered.length.toLocaleString()} products · ${periodLabel}`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.views, 0.1), color: COLORS.views }}
            />
            <Chip
              label={`基準 ATC ${(baseline * 100).toFixed(2)}%`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha('#64748B', 0.1), color: '#64748B' }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preset}
              onChange={(_, v) => { if (v) applyPreset(v as AllPreset); }}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none' } }}
            >
              <ToggleButton value="all">全部</ToggleButton>
              <ToggleButton value="gems">💎 潛力 {gemCount}</ToggleButton>
              <ToggleButton value="untested">❓ 未測試 {untestedCount}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
        action={
          <TextField
            size="small"
            placeholder="Search by name or SKU…"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onFilterChange('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          />
        }
        sx={{ pb: 1, pt: 2, px: 2 }}
      />
      {/* Totals strip */}
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Grid container spacing={2}>
          {[
            { label: 'Views', value: totals.views.toLocaleString(), color: COLORS.views },
            { label: 'ATC', value: totals.atc.toLocaleString(), color: COLORS.atc },
            { label: 'ATC Rate', value: totals.views > 0 ? `${(totals.atc / totals.views * 100).toFixed(2)}%` : '0%', color: COLORS.atc },
            { label: 'Orders', value: totals.purchases.toLocaleString(), color: COLORS.purchase },
            { label: 'CVR', value: totals.views > 0 ? `${(totals.purchases / totals.views * 100).toFixed(2)}%` : '0%', color: COLORS.purchase },
            { label: 'Units', value: totals.units.toLocaleString(), color: '#64748B' },
            { label: 'Revenue', value: `NT$${totals.revenue.toLocaleString()}`, color: COLORS.revenue },
          ].map(t => (
            <Grid item xs={6} sm={4} md={2} key={t.label}>
              <Box sx={{ p: 1, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{t.label}</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: t.color, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>{t.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      <CardContent sx={{ overflow: 'auto', height: 'calc(100vh - 360px)', minHeight: 600, p: 0 }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{
            '& th': { fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fff', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 },
            '& td': { fontSize: '0.8rem', py: 1 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2, width: 48 }}></TableCell>
                {headCell('product_title', 'Product', 'left')}
                {headCell('views', 'Views')}
                {headCell('add_to_carts', 'ATC')}
                {headCell('atc_rate', 'ATC Rate')}
                {headCell('potential', 'Potential')}
                {headCell('purchases', 'Orders')}
                {headCell('cvr', 'CVR')}
                {headCell('units', 'Units')}
                {headCell('revenue', 'Revenue')}
                {headCell('rpv', 'RPV')}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((p, idx) => (
                <TableRow
                  key={p.product_id}
                  sx={{
                    bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent',
                    '&:hover': { bgcolor: COLORS.tableHover },
                  }}
                >
                  <TableCell sx={{ pl: 2, py: 0.5, width: 48 }}>
                    <Avatar
                      src={p.image_url || undefined}
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: alpha('#94A3B8', 0.1), fontSize: '0.7rem', color: '#94A3B8' }}
                    >
                      {p.product_title?.charAt(0) || '?'}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, pl: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '0.7rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                            color: p.sku ? 'text.secondary' : 'text.disabled', minWidth: 34,
                          }}
                        >
                          {p.sku || '—'}
                        </Typography>
                        {(() => {
                          const url = productUrl(p);
                          const label = truncate(p.product_title, 60);
                          const base = { fontSize: '0.8rem', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 } as const;
                          return url ? (
                            <Link
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ ...base, color: 'inherit', '&:hover': { color: COLORS.views } }}
                            >
                              {label}
                            </Link>
                          ) : (
                            <Typography sx={base}>{label}</Typography>
                          );
                        })()}
                      </Box>
                      {p.source && p.source !== 'storefront' && (
                        <Chip
                          label={p.source}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600, bgcolor: alpha(COLORS.outlet, 0.1), color: COLORS.outlet, mt: 0.25 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p._views.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p._atc.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${(p._atc_rate * 100).toFixed(1)}%`}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                        bgcolor: p._atc_rate > 0.05 ? alpha(COLORS.purchase, 0.12) : alpha('#64748B', 0.08),
                        color: p._atc_rate > 0.05 ? COLORS.purchase : '#64748B',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {p._views < POTENTIAL_MIN_LEAD ? (
                      <MuiTooltip title={`只有 ${p._views} 次瀏覽 — 資料不足，無法判斷。需要曝光，不是排序。`}>
                        <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>
                      </MuiTooltip>
                    ) : (
                      <MuiTooltip
                        title={
                          p._judgeable
                            ? `真實 ATC 率至少 ${(p._lb * 100).toFixed(1)}%（95% 信心，${p._atc}/${p._views}）。基準 ${(baseline * 100).toFixed(2)}%。`
                            : `僅 ${p._views} 次瀏覽 — 參考用，非定論。要下定論需 ${POTENTIAL_MIN_JUDGE} 次以上。`
                        }
                      >
                        <Chip
                          label={`${(p._lb * 100).toFixed(1)}%${p._lead ? '?' : ''}`}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                            bgcolor: p._judgeable && p._lb > baseline ? alpha(COLORS.revenue, 0.15) : alpha('#64748B', 0.08),
                            color: p._judgeable && p._lb > baseline ? COLORS.revenue : '#64748B',
                            opacity: p._judgeable ? 1 : 0.65,
                          }}
                        />
                      </MuiTooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p._purchases.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${(p._cvr * 100).toFixed(2)}%`}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                        bgcolor: p._cvr > 0.02 ? alpha(COLORS.purchase, 0.12) : alpha('#64748B', 0.08),
                        color: p._cvr > 0.02 ? COLORS.purchase : '#64748B',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{p._units.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>NT${p._revenue.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {p._views > 0 ? `NT$${p._rpv.toFixed(1)}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>No products found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// --- Lead Time Table ---
// Per-SKU supply lead times. "Wait" = customer order → item received (how long
// customers actually wait for this SKU). "Supplier" = 叫貨 → received (how fast
// the manufacturer ships once ordered) — only populated since 2026-07-02 when
// sinsang_cart_log started recording, so it fills in over time.
type LeadTimeSortKey = 'sku' | 'product_name' | 'n_received' | 'wait_p50' | 'wait_p90' | 'pct_over_7' | 'pct_over_14' | 'supplier_p50' | 'last_received_at';

const LeadTimeTable: React.FC<{
  loading: boolean;
  rows: LeadTimeRow[];
  meta: LeadTimeMeta | null;
  days: number;
  onDaysChange: (d: number) => void;
  filter: string;
  onFilterChange: (v: string) => void;
  sortKey: LeadTimeSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: LeadTimeSortKey) => void;
}> = ({ loading, rows, meta, days, onDaysChange, filter, onFilterChange, sortKey, sortDir, onSort }) => {
  const filtered = filter
    ? rows.filter(r =>
        (r.product_name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.sku || '').toLowerCase().includes(filter.toLowerCase()))
    : rows;

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'product_name' || sortKey === 'sku') {
      return (a[sortKey] || '').localeCompare(b[sortKey] || '') * dir;
    }
    if (sortKey === 'last_received_at') {
      return (new Date(a.last_received_at).getTime() - new Date(b.last_received_at).getTime()) * dir;
    }
    const av = a[sortKey];
    const bv = b[sortKey];
    // null sorts to the bottom regardless of direction
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return (Number(av) - Number(bv)) * dir;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  const truncate = (str: string, maxLen: number) =>
    str && str.length > maxLen ? str.substring(0, maxLen) + '…' : str || '';

  const headCell = (key: LeadTimeSortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ pr: align === 'right' ? 2 : undefined, pl: align === 'left' ? 2 : undefined }}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'desc'}
        onClick={() => onSort(key)}
        sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary !important', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // Days chip: green ≤5, amber ≤10, red >10
  const daysChip = (v: number | null) => {
    if (v === null || v === undefined) return <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>;
    const color = v <= 5 ? COLORS.purchase : v <= 10 ? COLORS.atc : '#EF4444';
    return (
      <Chip
        label={`${v}d`}
        size="small"
        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', bgcolor: alpha(color, 0.12), color }}
      />
    );
  };

  const summary = meta?.summary;

  return (
    <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>SKU Lead Time</Typography>
            <Chip
              label={`${filtered.length.toLocaleString()} SKUs · last ${days} days`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.views, 0.1), color: COLORS.views }}
            />
            <ButtonGroup size="small">
              {[60, 120, 365].map(d => (
                <Button
                  key={d}
                  variant={days === d ? 'contained' : 'outlined'}
                  onClick={() => onDaysChange(d)}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}
                >
                  {d}d
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        }
        action={
          <TextField
            size="small"
            placeholder="Search name or SKU…"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onFilterChange('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          />
        }
        sx={{ pb: 1, pt: 2, px: 2 }}
      />
      {/* Summary strip */}
      {summary && (
        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Grid container spacing={2}>
            {[
              { label: 'Items Received', value: Number(summary.total_items).toLocaleString(), color: COLORS.views },
              { label: 'Median Wait', value: `${summary.wait_p50}d`, color: COLORS.purchase },
              { label: 'P90 Wait', value: `${summary.wait_p90}d`, color: COLORS.atc },
              { label: '> 7 Days', value: `${summary.pct_over_7}%`, color: COLORS.atc },
              { label: '> 14 Days', value: `${summary.pct_over_14}%`, color: '#EF4444' },
              {
                label: '叫貨 Log Since',
                value: meta?.sinsang_log?.log_start ? new Date(meta.sinsang_log.log_start).toLocaleDateString() : '—',
                color: '#64748B',
              },
            ].map(t => (
              <Grid item xs={6} sm={4} md={2} key={t.label}>
                <Box sx={{ p: 1, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 1.5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: t.color, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>{t.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <CardContent sx={{ overflow: 'auto', height: 'calc(100vh - 360px)', minHeight: 600, p: 0 }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{
            '& th': { fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fff', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 },
            '& td': { fontSize: '0.8rem', py: 1 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2, width: 48 }}></TableCell>
                {headCell('sku', 'SKU', 'left')}
                {headCell('product_name', 'Product', 'left')}
                {headCell('n_received', 'Received')}
                {headCell('wait_p50', 'Wait P50')}
                {headCell('wait_p90', 'Wait P90')}
                {headCell('pct_over_7', '> 7d')}
                {headCell('pct_over_14', '> 14d')}
                {headCell('supplier_p50', '叫貨→到貨')}
                {headCell('last_received_at', 'Last Received')}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((r, idx) => (
                <TableRow
                  key={r.sku}
                  sx={{
                    bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent',
                    '&:hover': { bgcolor: COLORS.tableHover },
                  }}
                >
                  <TableCell sx={{ pl: 2, py: 0.5, width: 48 }}>
                    <Avatar
                      src={r.image_url || undefined}
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: alpha('#94A3B8', 0.1), fontSize: '0.7rem', color: '#94A3B8' }}
                    >
                      {r.product_name?.charAt(0) || '?'}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ pl: 2, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.sku}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                      {truncate(r.product_name, 60)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(r.n_received).toLocaleString()}</TableCell>
                  <TableCell align="right">{daysChip(r.wait_p50)}</TableCell>
                  <TableCell align="right">{daysChip(r.wait_p90)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: Number(r.pct_over_7) >= 40 ? '#EF4444' : 'text.primary' }}>
                    {r.pct_over_7}%
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: Number(r.pct_over_14) >= 20 ? '#EF4444' : 'text.primary' }}>
                    {r.pct_over_14}%
                  </TableCell>
                  <TableCell align="right">
                    {r.supplier_p50 !== null && r.supplier_n ? (
                      <MuiTooltip title={`${r.supplier_n} items matched to a logged 叫貨 · P90 ${r.supplier_p90}d`}>
                        <span>{daysChip(r.supplier_p50)}</span>
                      </MuiTooltip>
                    ) : (
                      <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {r.last_received_at ? new Date(r.last_received_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>No receiving history found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// --- NOKE Counter Revenue ---
// Counter sales from noke_sales (the 大直 pop-up). Reported on their own tab and
// deliberately NOT part of storefront revenue — they create no orders rows.
interface NokeRevenue {
  summary: {
    tx_count: number;
    items_sold: number;
    gross: number;
    discount: number;
    net_total: number;
    avg_ticket: number;
    units_per_tx: number;
    vendor_totals: Record<string, number>;
    mdoc_attach_rate: number;
    member_tx_count: number;
    member_customers: number;
    reversed_count: number;
    reversed_total: number;
  };
  daily: Array<{ date: string; tx_count: number; items_sold: number; net_total: number; Metanoia?: number; MdoC?: number }>;
  salespeople: Array<{ salesperson: string; tx_count: number; items_sold: number; net_total: number }>;
  products: Array<{ sku: string | null; product_title: string | null; brand: string; units: number; revenue: number; tx_count: number }>;
}

const NokeRevenuePanel: React.FC<{
  loading: boolean;
  data: NokeRevenue | null;
  periodLabel: string;
}> = ({ loading, data, periodLabel }) => {
  const fmtNT = (v: number) => `NT$${Math.round(Number(v || 0)).toLocaleString()}`;
  const cardSx = { border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 };
  const headSx = { fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' as const };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }
  if (!data || data.summary.tx_count === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          No NOKE counter sales in this period ({periodLabel}).
        </Typography>
      </Box>
    );
  }

  const s = data.summary;
  const metanoia = Number(s.vendor_totals.Metanoia || 0);
  const mdoc = Number(s.vendor_totals.MdoC || 0);
  const maxSp = Math.max(1, ...data.salespeople.map(p => p.net_total));

  return (
    <>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mb: 1.5 }}>
        NOKE 門市 counter sales (completed, net of discounts, Asia/Taipei days) · {periodLabel}.
        Kept separate — NOT included in the storefront revenue on the Overview tab.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Counter Revenue"
            value={fmtNT(s.net_total)}
            icon={<MoneyIcon sx={{ fontSize: 20 }} />}
            color={COLORS.noke}
            subtitle={s.discount > 0 ? `Gross ${fmtNT(s.gross)} − discount ${fmtNT(s.discount)}` : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Transactions"
            value={s.tx_count.toLocaleString()}
            icon={<PurchaseIcon sx={{ fontSize: 20 }} />}
            color={COLORS.purchase}
            subtitle={s.reversed_count > 0 ? `${s.reversed_count} voided (${fmtNT(s.reversed_total)}) excluded` : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Avg Ticket"
            value={fmtNT(s.avg_ticket)}
            icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
            color={COLORS.revenue}
            subtitle={`${s.units_per_tx.toFixed(1)} items / sale`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Items Sold"
            value={s.items_sold.toLocaleString()}
            icon={<CartIcon sx={{ fontSize: 20 }} />}
            color={COLORS.atc}
            subtitle={`${s.member_tx_count} sales linked to a 會員`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={cardSx}>
            <CardHeader
              title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Daily Counter Revenue</Typography>}
              sx={{ pb: 0, pt: 2, px: 2 }}
            />
            <CardContent sx={{ pt: 1 }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} width={40} />
                  <Tooltip formatter={(v: any, name: any) => [fmtNT(Number(v)), name]} />
                  <Bar dataKey="Metanoia" stackId="rev" fill={COLORS.noke} />
                  <Bar dataKey="MdoC" stackId="rev" fill={COLORS.atc} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ ...cardSx, height: '100%' }}>
            <CardHeader
              title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Brand Split</Typography>}
              sx={{ pb: 0, pt: 2, px: 2 }}
            />
            <CardContent>
              {[
                { label: 'Metanoia', value: metanoia, color: COLORS.noke },
                { label: 'MdoC', value: mdoc, color: COLORS.atc },
              ].map(b => (
                <Box key={b.label} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{b.label}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNT(b.value)} · {s.net_total > 0 ? ((b.value / s.net_total) * 100).toFixed(0) : 0}%
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.gridLine }}>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: b.color, width: `${s.net_total > 0 ? (b.value / s.net_total) * 100 : 0}%` }} />
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                MdoC 連帶率 (sales with an MdoC item):{' '}
                <strong style={{ color: s.mdoc_attach_rate >= 0.15 ? '#10B981' : '#EF4444' }}>
                  {(s.mdoc_attach_rate * 100).toFixed(1)}%
                </strong>{' '}
                <span style={{ color: '#94A3B8' }}>(target ≥15%)</span>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={cardSx}>
            <CardHeader
              title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>By Salesperson</Typography>}
              sx={{ pb: 0, pt: 2, px: 2 }}
            />
            <CardContent>
              {data.salespeople.map(p => (
                <Box key={p.salesperson} sx={{ mb: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{p.salesperson}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNT(p.net_total)} · {p.tx_count} sales
                    </Typography>
                  </Box>
                  <Box sx={{ height: 5, borderRadius: 3, bgcolor: COLORS.gridLine }}>
                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: COLORS.noke, width: `${(p.net_total / maxSp) * 100}%` }} />
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={cardSx}>
            <CardHeader
              title={<Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Top Products at the Counter</Typography>}
              subheader={<Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Line revenue after campaign discounts, before sale-level discounts</Typography>}
              sx={{ pb: 0, pt: 2, px: 2 }}
            />
            <CardContent sx={{ pt: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>SKU</TableCell>
                      <TableCell sx={headSx}>Product</TableCell>
                      <TableCell sx={headSx} align="right">Units</TableCell>
                      <TableCell sx={headSx} align="right">Sales</TableCell>
                      <TableCell sx={headSx} align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.products.map((p, i) => (
                      <TableRow key={`${p.sku}-${i}`} sx={{ '&:nth-of-type(even)': { bgcolor: COLORS.tableBgAlt } }}>
                        <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{p.sku || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.brand === 'MdoC' && (
                            <Chip label="MdoC" size="small" sx={{ height: 18, fontSize: '0.6rem', mr: 0.75, bgcolor: alpha(COLORS.atc, 0.15), color: '#B45309' }} />
                          )}
                          {(p.product_title || '').replace(/^【Metanoia】\s*/, '')}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }} align="right">{p.units}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }} align="right">{p.tx_count}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} align="right">{fmtNT(p.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

// --- Looks Performance Table ---
type LooksSortKey = 'title' | 'impressions' | 'clicks' | 'ctr' | 'avg_position' | 'views' | 'item_clicks' | 'atc_count' | 'atc_rate' | 'purchases' | 'revenue' | 'rev_per_1k_impr';

const LooksPerformanceTable: React.FC<{
  loading: boolean;
  rows: LooksPerformanceRow[];
  totals: LooksTotals | null;
  days: number;
  onDaysChange: (d: number) => void;
  filter: string;
  onFilterChange: (v: string) => void;
  sortKey: LooksSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: LooksSortKey) => void;
}> = ({ loading, rows, totals, days, onDaysChange, filter, onFilterChange, sortKey, sortDir, onSort }) => {
  const filtered = filter
    ? rows.filter(r => (r.title || '').toLowerCase().includes(filter.toLowerCase()))
    : rows;

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '') * dir;
    }
    const av = a[sortKey];
    const bv = b[sortKey];
    // null (e.g. avg_position) sorts to the bottom regardless of direction
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const diff = (Number(av) - Number(bv)) * dir;
    // tie-break: impressions desc
    if (diff === 0) return Number(b.impressions) - Number(a.impressions);
    return diff;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  const truncate = (str: string, maxLen: number) =>
    str && str.length > maxLen ? str.substring(0, maxLen) + '…' : str || '';
  const fmtInt = (v: number) => Number(v || 0).toLocaleString();
  const fmtPct = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(1)}%`;
  const fmtNT = (v: number) => `NT$${Math.round(Number(v || 0)).toLocaleString()}`;

  const headCell = (key: LooksSortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ pr: align === 'right' ? 2 : undefined, pl: align === 'left' ? 2 : undefined }}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'desc'}
        onClick={() => onSort(key)}
        sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary !important', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const ctrTotal = totals && totals.impressions > 0 ? totals.clicks / totals.impressions : 0;

  return (
    <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Looks Performance</Typography>
            <Chip
              label={`${filtered.length.toLocaleString()} looks · last ${days} days`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.views, 0.1), color: COLORS.views }}
            />
            <ButtonGroup size="small">
              {[7, 14, 30, 90].map(d => (
                <Button
                  key={d}
                  variant={days === d ? 'contained' : 'outlined'}
                  onClick={() => onDaysChange(d)}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 1, textTransform: 'none' }}
                >
                  {d}d
                </Button>
              ))}
            </ButtonGroup>
          </Box>
        }
        action={
          <TextField
            size="small"
            placeholder="Search look title…"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onFilterChange('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          />
        }
        sx={{ pb: 1, pt: 2, px: 2 }}
      />
      {/* Summary strip */}
      {totals && (
        <Box sx={{ px: 2.5, pb: 1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Impressions" value={fmtInt(totals.impressions)} icon={<ViewsIcon sx={{ fontSize: 18 }} />} color={COLORS.views} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="CTR" value={`${(ctrTotal * 100).toFixed(1)}%`} icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color={COLORS.storefront} subtitle={`${fmtInt(totals.clicks)} clicks`} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Add-to-carts" value={fmtInt(totals.atcCount)} icon={<CartIcon sx={{ fontSize: 18 }} />} color={COLORS.atc} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Purchases" value={fmtInt(totals.purchases)} icon={<PurchaseIcon sx={{ fontSize: 18 }} />} color={COLORS.purchase} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard label="Revenue" value={fmtNT(totals.revenue)} icon={<MoneyIcon sx={{ fontSize: 18 }} />} color={COLORS.revenue} />
            </Grid>
          </Grid>
        </Box>
      )}
      <CardContent sx={{ overflow: 'auto', height: 'calc(100vh - 360px)', minHeight: 600, p: 0 }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{
            '& th': { fontWeight: 600, fontSize: '0.7rem', bgcolor: '#fff', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${COLORS.cardBorder}`, py: 1 },
            '& td': { fontSize: '0.8rem', py: 1 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2, width: 48 }}></TableCell>
                {headCell('title', 'Look', 'left')}
                {headCell('impressions', 'Impr')}
                {headCell('clicks', 'Clicks')}
                {headCell('ctr', 'CTR')}
                {headCell('avg_position', 'Avg Pos')}
                {headCell('views', 'Views')}
                {headCell('item_clicks', 'Item Clicks')}
                {headCell('atc_count', 'ATC')}
                {headCell('atc_rate', 'ATC Rate')}
                {headCell('purchases', 'Purchases')}
                {headCell('revenue', 'Revenue')}
                {headCell('rev_per_1k_impr', 'Rev/1k Impr')}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((r, idx) => (
                <TableRow
                  key={r.look_id}
                  sx={{
                    bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent',
                    '&:hover': { bgcolor: COLORS.tableHover },
                  }}
                >
                  <TableCell sx={{ pl: 2, py: 0.5, width: 48 }}>
                    <Avatar
                      src={r.hero_image_url || undefined}
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: alpha('#94A3B8', 0.1), fontSize: '0.7rem', color: '#94A3B8' }}
                    >
                      {r.title?.charAt(0) || '?'}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Link
                      href={`https://metanoia.tw/looks/${r.look_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}
                    >
                      {truncate(r.title, 48) || `Look #${r.look_id}`}
                    </Link>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
                      {[r.occasion, r.season, ...(r.derived_styles || []), ...(r.derived_colors || [])]
                        .filter((v): v is string => Boolean(v))
                        .slice(0, 5)
                        .map((v, i) => (
                          <Chip
                            key={`${v}-${i}`}
                            label={v}
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 500, bgcolor: alpha('#64748B', 0.08), color: '#64748B', '& .MuiChip-label': { px: 0.75 } }}
                          />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.impressions)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.clicks)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    <MuiTooltip title={`smoothed lower bound: ${fmtPct(r.ctr_lb)}`}>
                      <span>{fmtPct(r.ctr)}</span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {r.avg_position === null || r.avg_position === undefined ? '—' : Number(r.avg_position).toFixed(1)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.views)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.item_clicks)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.atc_count)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: Number(r.atc_rate) > 0 ? COLORS.atc : 'text.disabled' }}>
                    {fmtPct(r.atc_rate)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: Number(r.purchases) > 0 ? 600 : 400 }}>{fmtInt(r.purchases)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: Number(r.revenue) > 0 ? COLORS.purchase : 'text.disabled' }}>{fmtNT(r.revenue)}</TableCell>
                  <TableCell align="right" sx={{ pr: 2, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtNT(r.rev_per_1k_impr)}</TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                      No look events yet — data starts accruing once the storefront tracking deploys
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// --- Bakudan Table ---
const BakudanTable: React.FC<{
  loading: boolean;
  candidates: BakudanCandidate[];
  meta: { cached: boolean; computed_at: string; total: number } | null;
  filter: string;
  onFilterChange: (v: string) => void;
  productType: string;
  onProductTypeChange: (v: string) => void;
  vendor: string;
  onVendorChange: (v: string) => void;
  minViews: number;
  onMinViewsChange: (v: number) => void;
  minAtcRate: number;
  onMinAtcRateChange: (v: number) => void;
  sortKey: BakudanSortKey;
  onSortChange: (k: BakudanSortKey) => void;
}> = ({
  loading, candidates, meta, filter, onFilterChange,
  productType, onProductTypeChange, vendor, onVendorChange,
  minViews, onMinViewsChange, minAtcRate, onMinAtcRateChange,
  sortKey, onSortChange,
}) => {
  // Local in-memory filter for the name search (server handles the rest)
  const filtered = filter
    ? candidates.filter(c => (c.title || '').toLowerCase().includes(filter.toLowerCase())
                          || (c.sku || '').toLowerCase().includes(filter.toLowerCase()))
    : candidates;

  // Distinct product_types and vendors for dropdowns (from full candidate set, not filtered)
  const productTypes = Array.from(new Set(candidates.map(c => c.product_type).filter(Boolean))).sort();
  const vendors = Array.from(new Set(candidates.map(c => c.vendor).filter(Boolean))).sort();

  const fmtPct = (v: number | null) => v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`;
  const fmtNum = (v: number, d = 2) => v === null || v === undefined ? '—' : v.toFixed(d);

  const headCell = (key: BakudanSortKey, label: string) => (
    <TableCell align="right" sx={{ pr: 1.5 }}>
      <TableSortLabel
        active={sortKey === key}
        direction="desc"
        onClick={() => onSortChange(key)}
        sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary !important', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Bakudan Candidates</Typography>
            <Chip
              label={`${filtered.length.toLocaleString()} of ${meta?.total ?? candidates.length}`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(COLORS.purchase, 0.1), color: COLORS.purchase }}
            />
            {meta?.cached && (
              <Chip
                label={`cached @ ${new Date(meta.computed_at).toLocaleTimeString()}`}
                size="small"
                sx={{ height: 22, fontSize: '0.65rem', fontWeight: 500, bgcolor: alpha('#94A3B8', 0.1), color: '#64748B' }}
              />
            )}
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {/* Filter row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Type"
            value={productType}
            onChange={(e) => onProductTypeChange(e.target.value)}
            sx={{ minWidth: 130 }}
            SelectProps={{ native: true }}
          >
            <option value="">All types</option>
            {productTypes.map(t => <option key={t!} value={t!}>{t}</option>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Vendor"
            value={vendor}
            onChange={(e) => onVendorChange(e.target.value)}
            sx={{ minWidth: 130 }}
            SelectProps={{ native: true }}
          >
            <option value="">All vendors</option>
            {vendors.map(v => <option key={v!} value={v!}>{v}</option>)}
          </TextField>
          <TextField
            size="small"
            label="Min views (30d)"
            type="number"
            value={minViews}
            onChange={(e) => onMinViewsChange(parseInt(e.target.value, 10) || 0)}
            sx={{ width: 130 }}
            inputProps={{ min: 0, step: 50 }}
          />
          <TextField
            size="small"
            label="Min ATC%"
            type="number"
            value={(minAtcRate * 100).toFixed(0)}
            onChange={(e) => onMinAtcRateChange((parseFloat(e.target.value) || 0) / 100)}
            sx={{ width: 110 }}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortKey}
            onChange={(e) => onSortChange(e.target.value as BakudanSortKey)}
            sx={{ minWidth: 170 }}
            SelectProps={{ native: true }}
          >
            <option value="score">Bakudan score (composite)</option>
            <option value="discovery_score">Discovery score (hidden potential)</option>
            <option value="cross_basket_rate">Cross-basket w/ bakudan</option>
            <option value="returning_customer_rate">Returning-customer endorsement</option>
            <option value="multi_unit_rate">Multi-unit purchase rate</option>
            <option value="atc_rate">ATC rate</option>
            <option value="accel">Velocity acceleration</option>
            <option value="multi_variant_pct">Multi-variant attach</option>
            <option value="customer_return_rate">Customer return-to-brand rate</option>
            <option value="breadth_pct">Variant breadth</option>
            <option value="anchor_pct">Basket anchor</option>
            <option value="return_rate">Return rate</option>
            <option value="views">Views</option>
            <option value="qty_30d">Qty (30d)</option>
          </TextField>
          <TextField
            size="small"
            placeholder="Search SKU/name…"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onFilterChange('')}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ pl: 2 }}>#</TableCell>
                <TableCell sx={{ width: 48 }}></TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Title</TableCell>
                {headCell('score', 'Bakudan')}
                {headCell('discovery_score', 'Discovery')}
                {headCell('views', 'Views')}
                {headCell('atc_rate', 'ATC%')}
                {headCell('accel', 'Accel')}
                {headCell('qty_30d', 'Qty 30d')}
                {headCell('cross_basket_rate', 'X-Basket')}
                {headCell('returning_customer_rate', 'Repeat Buy')}
                {headCell('multi_unit_rate', 'Multi-Unit')}
                {headCell('multi_variant_pct', 'Multi-var')}
                {headCell('breadth_pct', 'Breadth')}
                {headCell('anchor_pct', 'Anchor')}
                {headCell('return_rate', 'Return%')}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c, idx) => {
                const domain = c.vendor === 'MdoC' || (c.sku || '').startsWith('M') ? 'mdoc.tw' : 'metanoia.tw';
                const url = `https://${domain}/shop/${c.handle}`;
                return (
                  <TableRow
                    key={c.id}
                    sx={{
                      bgcolor: idx % 2 === 1 ? COLORS.tableBgAlt : 'transparent',
                      '&:hover': { bgcolor: COLORS.tableHover },
                    }}
                  >
                    <TableCell sx={{ pl: 2, py: 0.5, fontWeight: 700, color: 'text.secondary' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 0.5, width: 48 }}>
                      <Avatar
                        src={c.image_url || undefined}
                        variant="rounded"
                        sx={{ width: 36, height: 36, bgcolor: alpha('#94A3B8', 0.1), fontSize: '0.7rem', color: '#94A3B8' }}
                      >
                        {c.title?.charAt(0) || '?'}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{c.sku || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.8rem', color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: COLORS.purchase } }}
                      >
                        {c.title}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={c.score.toFixed(3)}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.7rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          bgcolor: c.score > 0.4 ? alpha(COLORS.purchase, 0.15) : alpha('#94A3B8', 0.08),
                          color: c.score > 0.4 ? COLORS.purchase : '#64748B',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {c.is_proven_bakudan ? (
                        <Chip
                          label="bakudan"
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, fontStyle: 'italic',
                                bgcolor: alpha('#94A3B8', 0.08), color: '#64748B' }}
                        />
                      ) : c.discovery_score !== null ? (
                        <Chip
                          label={c.discovery_score.toFixed(3)}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.7rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                            bgcolor: c.discovery_score > 0.5 ? alpha('#F59E0B', 0.15) : alpha('#94A3B8', 0.08),
                            color: c.discovery_score > 0.5 ? '#B45309' : '#64748B',
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{c.views.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.atc_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtNum(c.accel)}x</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{c.qty_30d}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.cross_basket_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.returning_customer_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.multi_unit_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.multi_variant_pct)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.breadth_pct)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.anchor_pct)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{fmtPct(c.return_rate)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default AnalyticsDashboardPage;
